import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Button, Chip, Avatar, Divider,
  IconButton, Tooltip, CircularProgress, Stack, Grid,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Alert, Tabs, Tab, Checkbox, FormControlLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PetsIcon from '@mui/icons-material/Pets';
import VaccinesIcon from '@mui/icons-material/Vaccines';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import ScienceIcon from '@mui/icons-material/Science';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MedicationIcon from '@mui/icons-material/Medication';
import BugReportIcon from '@mui/icons-material/BugReport';
import PrintIcon from '@mui/icons-material/Print';
import BrokenImageIcon from '@mui/icons-material/BrokenImage';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import { LineChart, Line, XAxis, YAxis, Tooltip as RechartTooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  collection, query, where, orderBy, onSnapshot,
  getDoc, getDocs, doc, updateDoc, serverTimestamp, addDoc, deleteDoc,
} from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useBranch } from '../context/BranchContext';
import { crearPresupuestoAutomatico } from '../services/presupuestoAutoService';
import { generateInterconsultaPDF } from '../services/interconsultaPDF';
import { jsPDF } from 'jspdf';
import { normalizeBranch, loadLogos, renderStandardHeader, renderStandardFooter, COLORS_PDF } from '../services/pdfCommon';

const PRIMARY = '#1A3A5C';
const TEAL    = '#2CB8A0';
const DANGER  = '#DC2626';
const WARNING = '#F59E0B';
const SUCCESS = '#10B981';

function calcAge(birthDate) {
  if (!birthDate) return '—';
  const b = new Date(birthDate);
  const now = new Date();
  const years = Math.floor((now - b) / (365.25 * 864e5));
  if (years >= 1) return `${years} año${years !== 1 ? 's' : ''}`;
  const months = Math.floor((now - b) / (30.44 * 864e5));
  if (months >= 1) return `${months} mes${months !== 1 ? 'es' : ''}`;
  return '< 1 mes';
}

function fmtDate(val) {
  if (!val) return '—';
  const d = val?.toDate ? val.toDate() : new Date(val);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDateTime(val) {
  if (!val) return '—';
  const d = val?.toDate ? val.toDate() : new Date(val);
  return d.toLocaleString('es-CL', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function toDate(v) {
  if (!v) return null;
  if (typeof v.toDate === 'function') return v.toDate();
  return new Date(v);
}
function calcHospDays(admissionAt, altaAt) {
  const d = toDate(admissionAt);
  if (!d) return 0;
  const fin = toDate(altaAt) || new Date();
  return Math.max(1, Math.ceil((fin - d) / 86400000));
}

function fmtDateShort(val) {
  if (!val) return '';
  const d = val?.toDate ? val.toDate() : new Date(val);
  return d.toLocaleDateString('es-CL', { day: '2-digit', month: 'short' });
}

/** Retorna estado semántico de vacuna/antiparasitario según fecha próxima */
function proximaEstado(proxima) {
  if (!proxima) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const p = new Date(proxima);
  const diff = Math.floor((p - today) / 86400000);
  if (diff < 0)  return { label: 'Vencida',  bg: '#FEE2E2', color: DANGER };
  if (diff <= 30) return { label: 'Próxima',  bg: '#FEF9C3', color: WARNING };
  return             { label: 'Al día',    bg: '#DCFCE7', color: SUCCESS };
}

/** Calcula tendencia de peso entre última y penúltima consulta */
function pesoTendencia(weights) {
  const pesos = weights
    .filter(w => w.weight != null && w.weight !== '')
    .map(w => parseFloat(w.weight));
  if (pesos.length < 2) return pesos.length === 1 ? { actual: pesos[0], icono: '─', color: 'text.secondary' } : null;
  const actual = pesos[0], anterior = pesos[1];
  const diff = actual - anterior;
  const pct = (Math.abs(diff) / anterior) * 100;
  if (pct < 1) return { actual, icono: '─', color: 'text.secondary' };
  return diff > 0
    ? { actual, icono: '▲', color: DANGER,  diff: `+${diff.toFixed(1)}` }
    : { actual, icono: '▼', color: TEAL,    diff: diff.toFixed(1) };
}

const VISIT_CHIP = {
  consulta:   { label: 'Consulta',   bg: '#DBEAFE', color: '#1D4ED8' },
  control:    { label: 'Control',    bg: '#D1FAE5', color: '#065F46' },
  vacuna:     { label: 'Vacunación', bg: '#FEF9C3', color: '#854D0E' },
  cirugia:    { label: 'Cirugía',    bg: '#FCE7F3', color: '#9D174D' },
  emergencia: { label: 'Emergencia', bg: '#FEE2E2', color: '#991B1B' },
  bienestar:  { label: 'Bienestar', bg: '#F3E8FF', color: '#6B21A8' },
  toma_muestra: { label: 'Toma de muestra', bg: '#E0F2FE', color: '#0369A1' },
};

// ── WeightChart ──────────────────────────────────────────────────────────────
function WeightChart({ weights, pesoIdeal }) {
  const data = useMemo(() => weights
    .filter(w => w.weight != null && w.weight !== '')
    .map(w => ({ peso: parseFloat(w.weight), fecha: fmtDateShort(w.date), fechaFull: fmtDate(w.date) }))
    .reverse(), [weights]);

  if (data.length === 0) return (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      <FitnessCenterIcon sx={{ fontSize: 28, color: '#CBD5E1', mb: 0.5 }} />
      <Typography variant="caption" color="text.disabled" display="block">Sin registros de peso</Typography>
    </Box>
  );
  if (data.length === 1) return (
    <Box sx={{ textAlign: 'center', py: 1 }}>
      <Typography variant="h6" fontWeight={700} color={TEAL}>{data[0].peso} kg</Typography>
      <Typography variant="caption" color="text.secondary">{data[0].fechaFull}</Typography>
    </Box>
  );

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <Paper elevation={2} sx={{ p: 1, borderRadius: 2 }}>
        <Typography variant="caption" display="block" fontWeight={700}>{payload[0].payload.fechaFull}</Typography>
        <Typography variant="caption" color={TEAL} fontWeight={700}>{payload[0].value} kg</Typography>
      </Paper>
    );
  };
  const pesos = data.map(d => d.peso);
  return (
    <ResponsiveContainer width="100%" height={120}>
      <LineChart data={data} margin={{ top: 4, right: 8, left: -24, bottom: 0 }}>
        <XAxis dataKey="fecha" tick={{ fontSize: 9, fill: '#94A3B8' }} />
        <YAxis domain={[Math.max(0, Math.min(...pesos) - 0.5), Math.max(...pesos) + 0.5]} tick={{ fontSize: 9, fill: '#94A3B8' }} />
        <RechartTooltip content={<CustomTooltip />} />
        {pesoIdeal && <ReferenceLine y={pesoIdeal} stroke={WARNING} strokeDasharray="3 3" label={{ value: 'Ideal', fontSize: 9, fill: WARNING }} />}
        <Line type="monotone" dataKey="peso" stroke={TEAL} strokeWidth={2} dot={{ r: 3, fill: TEAL }} activeDot={{ r: 5 }} />
      </LineChart>
    </ResponsiveContainer>
  );
}

// ── ActiveAlertsPanel ────────────────────────────────────────────────────────
function ActiveAlertsPanel({ patient }) {
  const alerts = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const in30  = new Date(today); in30.setDate(in30.getDate() + 30);
    const vacunas = patient?.vacunas || [];
    const medsActivos = (patient?.medicamentos_activos || []).filter(m =>
      !m.fecha_fin || new Date(m.fecha_fin) >= today
    );
    const vacVencidas = vacunas.filter(v => v.proxima && new Date(v.proxima) < today);
    const vacProximas = vacunas.filter(v => v.proxima && new Date(v.proxima) >= today && new Date(v.proxima) <= in30);
    const antiProximos = (patient?.antiparasitarios || [])
      .filter(a => a.proxima && new Date(a.proxima) >= today && new Date(a.proxima) <= in30);
    return { alergias: patient?.alergias || '', medsActivos, vacVencidas, vacProximas, antiProximos };
  }, [patient]);

  const hasAny = alerts.alergias || alerts.medsActivos.length || alerts.vacVencidas.length || alerts.vacProximas.length || alerts.antiProximos.length;
  if (!hasAny) return null;

  return (
    <Stack spacing={0.7} sx={{ mb: 1.5 }}>
      {alerts.alergias && (
        <Alert severity="error" icon={<WarningAmberIcon fontSize="small" />}
          sx={{ py: 0.3, fontSize: '0.75rem', borderRadius: 2, '& .MuiAlert-message': { pt: '3px' } }}>
          <strong>Alergia:</strong> {alerts.alergias}
        </Alert>
      )}
      {alerts.vacVencidas.length > 0 && (
        <Alert severity="error" sx={{ py: 0.3, fontSize: '0.75rem', borderRadius: 2, '& .MuiAlert-message': { pt: '3px' } }}>
          <strong>Vacuna vencida:</strong> {alerts.vacVencidas.map(v => v.vacuna).join(', ')}
        </Alert>
      )}
      {alerts.vacProximas.length > 0 && (
        <Alert severity="warning" sx={{ py: 0.3, fontSize: '0.75rem', borderRadius: 2, '& .MuiAlert-message': { pt: '3px' } }}>
          <strong>Vacuna próxima:</strong> {alerts.vacProximas.map(v => `${v.vacuna} (${v.proxima})`).join(', ')}
        </Alert>
      )}
      {alerts.medsActivos.length > 0 && (
        <Alert severity="info" icon={<MedicationIcon fontSize="small" />}
          sx={{ py: 0.3, fontSize: '0.75rem', borderRadius: 2, '& .MuiAlert-message': { pt: '3px' } }}>
          <strong>Medicamentos activos:</strong> {alerts.medsActivos.map(m => m.nombre).join(', ')}
        </Alert>
      )}
      {alerts.antiProximos.length > 0 && (
        <Alert severity="warning" sx={{ py: 0.3, fontSize: '0.75rem', borderRadius: 2, '& .MuiAlert-message': { pt: '3px' } }}>
          <strong>Antiparasitario próximo:</strong> {alerts.antiProximos.map(a => a.producto).join(', ')}
        </Alert>
      )}
    </Stack>
  );
}

// ── MedicalProfileDialog ─────────────────────────────────────────────────────
function MedicalProfileDialog({ open, onClose, patient, patientId }) {
  const [profile, setProfile] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open && patient) {
      setProfile({
        microchip: patient.microchip || '',
        esterilizado: patient.esterilizado ?? '',
        esterilizacionFecha: patient.esterilizacionFecha || '',
        alergias: patient.alergias || '',
        condicionesCronicas: patient.condicionesCronicas || '',
        notasMedicas: patient.notasMedicas || '',
        grupoSanguineo: patient.grupoSanguineo || '',
        peso_objetivo: patient.peso_objetivo || '',
        entorno: patient.entorno || '',
        dieta_marca: patient.dieta?.marca || '',
        dieta_tipo: patient.dieta?.tipo || '',
      });
    }
  }, [open, patient]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const { dieta_marca, dieta_tipo, ...rest } = profile;
      await updateDoc(doc(db, 'patients', patientId), {
        ...rest,
        dieta: { marca: dieta_marca, tipo: dieta_tipo },
        updatedAt: serverTimestamp(),
      });
      onClose(true);
    } catch (e) { /* ignore */ }
    setSaving(false);
  };

  const p = (f) => profile[f] ?? '';
  const set = (f) => (e) => setProfile((prev) => ({ ...prev, [f]: e.target.value }));

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: PRIMARY }}>Perfil Médico Permanente</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid size={8}><TextField label="Microchip / Tatuaje" fullWidth size="small" value={p('microchip')} onChange={set('microchip')} /></Grid>
          <Grid size={4}><TextField label="Grupo sanguíneo" fullWidth size="small" value={p('grupoSanguineo')} onChange={set('grupoSanguineo')} /></Grid>
          <Grid size={6}>
            <TextField select label="Esterilizado/a" fullWidth size="small" value={p('esterilizado')} onChange={set('esterilizado')}>
              <MenuItem value="">—</MenuItem>
              <MenuItem value={false}>No</MenuItem>
              <MenuItem value={true}>Sí</MenuItem>
            </TextField>
          </Grid>
          <Grid size={6}><TextField label="Fecha esterilización" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={p('esterilizacionFecha')} onChange={set('esterilizacionFecha')} /></Grid>
          <Grid size={6}><TextField label="Peso ideal (kg)" type="number" fullWidth size="small" value={p('peso_objetivo')} onChange={set('peso_objetivo')} /></Grid>
          <Grid size={6}>
            <TextField select label="Entorno" fullWidth size="small" value={p('entorno')} onChange={set('entorno')}>
              <MenuItem value="">—</MenuItem>
              <MenuItem value="indoor">Indoor</MenuItem>
              <MenuItem value="outdoor">Outdoor</MenuItem>
              <MenuItem value="mixto">Mixto</MenuItem>
            </TextField>
          </Grid>
          <Grid size={6}><TextField label="Dieta — Marca" fullWidth size="small" value={p('dieta_marca')} onChange={set('dieta_marca')} /></Grid>
          <Grid size={6}>
            <TextField select label="Dieta — Tipo" fullWidth size="small" value={p('dieta_tipo')} onChange={set('dieta_tipo')}>
              <MenuItem value="">—</MenuItem>
              <MenuItem value="seco">Seco (croquetas)</MenuItem>
              <MenuItem value="humedo">Húmedo</MenuItem>
              <MenuItem value="raw">Raw / BARF</MenuItem>
              <MenuItem value="mixto">Mixto</MenuItem>
            </TextField>
          </Grid>
          <Grid size={12}><TextField label="Alergias conocidas" fullWidth size="small" multiline rows={2} placeholder="Ej: Penicilina (urticaria)" value={p('alergias')} onChange={set('alergias')} /></Grid>
          <Grid size={12}><TextField label="Condiciones crónicas" fullWidth size="small" multiline rows={2} placeholder="Ej: Diabetes mellitus (dx 2024)" value={p('condicionesCronicas')} onChange={set('condicionesCronicas')} /></Grid>
          <Grid size={12}><TextField label="Notas médicas generales" fullWidth size="small" multiline rows={2} value={p('notasMedicas')} onChange={set('notasMedicas')} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ bgcolor: PRIMARY }}>{saving ? 'Guardando…' : 'Guardar'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── VaccineDialog ─────────────────────────────────────────────────────────────
function VaccineDialog({ open, onClose, patientId, patient, owner, existing }) {
  const empty = { arancelIds: [], vacuna: '', fecha: '', proxima: '', perVacuna: {}, veterinario: '', veterinarioId: '', presupuestoId: '', presupuestoNumber: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [solicitando, setSolicitando] = useState(false);
  const [aranceles, setAranceles] = useState([]);
  const { currentUser, userData } = useAuth();
  const { activeBranchId, filterByBranch } = useBranch();

  useEffect(() => {
    if (!open) return;
    const base = existing || empty;
    const arancelIds = existing?.arancelIds || (existing?.arancelId ? [existing.arancelId] : []);
    const veterinario = existing?.veterinario || userData?.name || currentUser?.email || '';
    const veterinarioId = existing?.veterinarioId || userData?.professionalId || currentUser?.uid || '';
    const today = new Date().toLocaleDateString('en-CA');
    const nextYear = new Date(); nextYear.setFullYear(nextYear.getFullYear() + 1);
    const proxima = nextYear.toLocaleDateString('en-CA');
    const perVacuna = {};
    arancelIds.forEach((id, idx) => {
      const fromExisting = existing?.perVacuna?.[id];
      perVacuna[id] = {
        lote: fromExisting?.lote || (idx === 0 ? existing?.lote : '') || '',
        laboratorio: fromExisting?.laboratorio || (idx === 0 ? existing?.laboratorio : '') || '',
      };
    });
    setForm({ ...base, arancelIds, vacuna: existing?.vacuna || '', veterinario, veterinarioId, fecha: base?.fecha || today, proxima: base?.proxima || proxima, perVacuna });
    const load = async () => {
      try {
        const aSnap = await getDocs(collection(db, 'aranceles'));
        const all = aSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((a) => a.activo !== false);
        const vacs = filterByBranch(all, { includeUnassigned: true })
          .filter((a) => (a.tipoPrestacionName || a.tipoPrestacion || '').toLowerCase().includes('vacun'));
        setAranceles(vacs.sort((a, b) => (a.prestacion || '').localeCompare(b.prestacion || '', 'es', { sensitivity: 'base' })));
      } catch (_) {}
    };
    load();
  }, [open, existing]);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const handleArancelChange = (e) => {
    const raw = e.target.value;
    const ids = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    const selected = aranceles.filter((a) => ids.includes(a.id));
    const perVacuna = { ...form.perVacuna };
    ids.forEach((id) => { if (!perVacuna[id]) perVacuna[id] = { lote: '', laboratorio: '' }; });
    Object.keys(perVacuna).forEach((id) => { if (!ids.includes(id)) delete perVacuna[id]; });
    setForm((p) => ({ ...p, arancelIds: ids, vacuna: selected.map((a) => a.prestacion).join(', '), perVacuna }));
  };

  const setPer = (arancelId, campo) => (e) => {
    setForm((p) => ({
      ...p,
      perVacuna: { ...p.perVacuna, [arancelId]: { ...p.perVacuna?.[arancelId], [campo]: e.target.value } },
    }));
  };

  const handleSave = async () => {
    const arancelIds = Array.isArray(form.arancelIds) ? form.arancelIds : (form.arancelIds ? [form.arancelIds] : []);
    if (!form.vacuna || !form.fecha || !arancelIds.length) return;
    setSaving(true);
    try {
      const snap = await getDoc(doc(db, 'patients', patientId));
      const vaccines = snap.data()?.vacunas || [];
      const common = { fecha: form.fecha, proxima: form.proxima, veterinario: form.veterinario, veterinarioId: form.veterinarioId, presupuestoId: form.presupuestoId, presupuestoNumber: form.presupuestoNumber };
      const selected = aranceles.filter((a) => arancelIds.includes(a.id));
      if (existing?._idx !== undefined) {
        const arancel = selected[0];
        const pVac = form.perVacuna?.[arancel?.id] || {};
        vaccines[existing._idx] = { ...common, arancelId: arancel?.id || form.arancelIds[0], vacuna: arancel?.prestacion || form.vacuna, lote: pVac?.lote || '', laboratorio: pVac?.laboratorio || '' };
      } else {
        selected.forEach((arancel) => {
          const pVac = form.perVacuna?.[arancel.id] || {};
          vaccines.push({ ...common, arancelId: arancel.id, vacuna: arancel.prestacion, lote: pVac?.lote || '', laboratorio: pVac?.laboratorio || '' });
        });
      }
      await updateDoc(doc(db, 'patients', patientId), { vacunas: vaccines, updatedAt: serverTimestamp() });
      onClose(true);
    } catch (e) { console.error('Error al guardar vacunas:', e); alert('Error al guardar: ' + (e?.message || e)); }
    setSaving(false);
  };

  const handleSolicitar = async () => {
    const arancelIds = Array.isArray(form.arancelIds) ? form.arancelIds : (form.arancelIds ? [form.arancelIds] : []);
    if (!form.vacuna || !arancelIds.length) return;
    setSolicitando(true);
    try {
      const presupuesto = await crearPresupuestoAutomatico({
        orderId: null,
        orderType: 'vacuna',
        orderNumber: '',
        patientId,
        patientName: patient?.name || '',
        patientSpecies: patient?.species || '',
        ownerId: patient?.ownerId || '',
        ownerName: owner?.name || '',
        ownerPhone: owner?.celular || '',
        convenioName: owner?.convenio || '',
        branchId: activeBranchId || userData?.branchId || '',
        vetId: form.veterinarioId || userData?.professionalId || currentUser?.uid || '',
        vetName: form.veterinario || userData?.name || currentUser?.email || '',
        items: arancelIds.map((aid) => ({
          arancel: aranceles.find((a) => a.id === aid),
          cantidad: 1,
          tipoPrestacionFallback: 'Vacunación',
        })),
        notas: `Vacunas solicitadas: ${form.vacuna}`,
      });
      setForm((p) => ({ ...p, presupuestoId: presupuesto.id, presupuestoNumber: presupuesto.numero }));
    } catch (e) {
      console.error('Error al solicitar vacuna:', e);
      alert('Error al crear presupuesto: ' + (e?.message || JSON.stringify(e)));
    }
    setSolicitando(false);
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: PRIMARY }}>{existing?._idx !== undefined ? 'Editar vacuna' : 'Registrar vacuna'}</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              select
              SelectProps={{ multiple: true, renderValue: (selected) => { const sel = Array.isArray(selected) ? selected : (selected ? [selected] : []); return aranceles.filter((a) => sel.includes(a.id)).map((a) => a.prestacion).join(', '); } }}
              label="Vacunas *"
              fullWidth
              size="small"
              value={form.arancelIds}
              onChange={handleArancelChange}
            >
              {aranceles.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.prestacion}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={6}><TextField label="Fecha aplicación *" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.fecha} onChange={set('fecha')} /></Grid>
          <Grid size={6}><TextField label="Próxima dosis" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.proxima} onChange={set('proxima')} /></Grid>
          {aranceles.filter((a) => form.arancelIds.includes(a.id)).map((arancel) => (
            <React.Fragment key={arancel.id}>
              <Grid size={12}><Typography variant="caption" fontWeight={600} color={PRIMARY}>{arancel.prestacion}</Typography></Grid>
              <Grid size={6}><TextField label="Lote" fullWidth size="small" value={form.perVacuna?.[arancel.id]?.lote || ''} onChange={setPer(arancel.id, 'lote')} /></Grid>
              <Grid size={6}><TextField label="Laboratorio" fullWidth size="small" value={form.perVacuna?.[arancel.id]?.laboratorio || ''} onChange={setPer(arancel.id, 'laboratorio')} /></Grid>
            </React.Fragment>
          ))}
          <Grid size={12}><TextField label="Veterinario que aplicó" fullWidth size="small" value={form.veterinario} onChange={set('veterinario')} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancelar</Button>
        <Button variant="outlined" onClick={handleSolicitar} disabled={solicitando || !form.arancelIds?.length || !form.vacuna || !!form.presupuestoId} sx={{ color: PRIMARY, borderColor: PRIMARY }}>
          {solicitando ? 'Solicitando…' : (form.presupuestoNumber || 'Solicitar')}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !form.vacuna || !form.fecha} sx={{ bgcolor: PRIMARY }}>{saving ? 'Confirmando…' : 'Confirmo administración'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── AntiparasiticDialog ───────────────────────────────────────────────────────
function AntiparasiticDialog({ open, onClose, patientId, patient, owner, existing }) {
  const empty = { arancelIds: [], producto: '', cantidad: 1, fecha: '', proxima: '', perProducto: {}, veterinario: '', veterinarioId: '', presupuestoId: '', presupuestoNumber: '' };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [solicitando, setSolicitando] = useState(false);
  const [aranceles, setAranceles] = useState([]);
  const { currentUser, userData } = useAuth();
  const { activeBranchId, filterByBranch } = useBranch();

  useEffect(() => {
    if (!open) return;
    const base = existing || empty;
    const arancelIds = existing?.arancelIds || (existing?.arancelId ? [existing.arancelId] : []);
    const veterinario = existing?.veterinario || userData?.name || currentUser?.email || '';
    const veterinarioId = existing?.veterinarioId || userData?.professionalId || currentUser?.uid || '';
    const today = new Date().toLocaleDateString('en-CA');
    const nextYear = new Date(); nextYear.setFullYear(nextYear.getFullYear() + 1);
    const proxima = nextYear.toLocaleDateString('en-CA');
    const perProducto = {};
    arancelIds.forEach((id) => {
      const fromExisting = existing?.perProducto?.[id];
      perProducto[id] = {
        cantidad: fromExisting?.cantidad || existing?.cantidad || 1,
      };
    });
    setForm({ ...base, arancelIds, producto: existing?.producto || '', veterinario, veterinarioId, fecha: base?.fecha || today, proxima: base?.proxima || proxima, perProducto });
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, 'aranceles'));
        const all = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((a) => a.activo !== false);
        const prods = filterByBranch(all, { includeUnassigned: true })
          .filter((a) => (a.tipoPrestacionName || a.tipoPrestacion || '').toLowerCase().includes('antiparasit'));
        setAranceles(prods.sort((a, b) => (a.prestacion || '').localeCompare(b.prestacion || '', 'es', { sensitivity: 'base' })));
      } catch (_) {}
    };
    load();
  }, [open, existing]);

  const set = (f) => (e) => setForm((p) => ({ ...p, [f]: e.target.value }));

  const handleArancelChange = (e) => {
    const raw = e.target.value;
    const ids = Array.isArray(raw) ? raw : (raw ? [raw] : []);
    const selected = aranceles.filter((a) => ids.includes(a.id));
    const perProducto = { ...form.perProducto };
    ids.forEach((id) => { if (!perProducto[id]) perProducto[id] = { cantidad: 1 }; });
    Object.keys(perProducto).forEach((id) => { if (!ids.includes(id)) delete perProducto[id]; });
    setForm((p) => ({ ...p, arancelIds: ids, producto: selected.map((a) => a.prestacion).join(', '), perProducto }));
  };

  const setPer = (arancelId, campo) => (e) => {
    setForm((p) => ({
      ...p,
      perProducto: { ...p.perProducto, [arancelId]: { ...p.perProducto?.[arancelId], [campo]: Number(e.target.value) || 0 } },
    }));
  };

  const handleSolicitar = async () => {
    const arancelIds = Array.isArray(form.arancelIds) ? form.arancelIds : (form.arancelIds ? [form.arancelIds] : []);
    if (!form.producto || !arancelIds.length) return;
    setSolicitando(true);
    try {
      const presupuesto = await crearPresupuestoAutomatico({
        orderId: null,
        orderType: 'antiparasitario',
        orderNumber: '',
        patientId,
        patientName: patient?.name || '',
        patientSpecies: patient?.species || '',
        ownerId: patient?.ownerId || '',
        ownerName: owner?.name || '',
        ownerPhone: owner?.celular || '',
        convenioName: owner?.convenio || '',
        branchId: activeBranchId || userData?.branchId || '',
        vetId: form.veterinarioId || userData?.professionalId || currentUser?.uid || '',
        vetName: form.veterinario || userData?.name || currentUser?.email || '',
        items: arancelIds.map((aid) => ({
          arancel: aranceles.find((a) => a.id === aid),
          cantidad: Math.max(1, Number(form.perProducto?.[aid]?.cantidad) || 1),
          tipoPrestacionFallback: 'Antiparasitario',
        })),
        notas: `Antiparasitarios solicitados: ${form.producto}`,
      });
      setForm((p) => ({ ...p, presupuestoId: presupuesto.id, presupuestoNumber: presupuesto.numero }));
    } catch (e) {
      console.error('Error al solicitar antiparasitario:', e);
      alert('Error al crear presupuesto: ' + (e?.message || JSON.stringify(e)));
    }
    setSolicitando(false);
  };

  const handleSave = async () => {
    const arancelIds = Array.isArray(form.arancelIds) ? form.arancelIds : (form.arancelIds ? [form.arancelIds] : []);
    if (!form.producto || !form.fecha || !arancelIds.length) return;
    setSaving(true);
    try {
      const snap = await getDoc(doc(db, 'patients', patientId));
      const list = snap.data()?.antiparasitarios || [];
      const common = { fecha: form.fecha, proxima: form.proxima, veterinario: form.veterinario, veterinarioId: form.veterinarioId, presupuestoId: form.presupuestoId, presupuestoNumber: form.presupuestoNumber };
      const selected = aranceles.filter((a) => arancelIds.includes(a.id));
      if (existing?._idx !== undefined) {
        const arancel = selected[0];
        const pProd = form.perProducto?.[arancel?.id] || {};
        list[existing._idx] = { ...common, arancelId: arancel?.id || form.arancelIds[0], producto: arancel?.prestacion || form.producto, cantidad: pProd?.cantidad || 1 };
      } else {
        selected.forEach((arancel) => {
          const pProd = form.perProducto?.[arancel.id] || {};
          list.push({ ...common, arancelId: arancel.id, producto: arancel.prestacion, cantidad: pProd?.cantidad || 1 });
        });
      }
      await updateDoc(doc(db, 'patients', patientId), { antiparasitarios: list, updatedAt: serverTimestamp() });
      onClose(true);
    } catch (e) { console.error('Error al guardar antiparasitarios:', e); alert('Error al guardar: ' + (e?.message || e)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: TEAL }}>{existing?._idx !== undefined ? 'Editar antiparasitario' : 'Registrar antiparasitario'}</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              select
              SelectProps={{ multiple: true, renderValue: (selected) => { const sel = Array.isArray(selected) ? selected : (selected ? [selected] : []); return aranceles.filter((a) => sel.includes(a.id)).map((a) => a.prestacion).join(', '); } }}
              label="Productos *"
              fullWidth
              size="small"
              value={form.arancelIds}
              onChange={handleArancelChange}
            >
              {aranceles.map((a) => (
                <MenuItem key={a.id} value={a.id}>{a.prestacion}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={6}><TextField label="Fecha aplicación *" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.fecha} onChange={set('fecha')} /></Grid>
          <Grid size={6}><TextField label="Próxima dosis" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.proxima} onChange={set('proxima')} /></Grid>
          {aranceles.filter((a) => form.arancelIds.includes(a.id)).map((arancel) => (
            <React.Fragment key={arancel.id}>
              <Grid size={12}><Typography variant="caption" fontWeight={600} color={TEAL}>{arancel.prestacion}</Typography></Grid>
              <Grid size={12}><TextField label="Cantidad" type="number" fullWidth size="small" inputProps={{ min: 1 }} value={form.perProducto?.[arancel.id]?.cantidad || 1} onChange={setPer(arancel.id, 'cantidad')} /></Grid>
            </React.Fragment>
          ))}
          <Grid size={12}><TextField label="Veterinario" fullWidth size="small" value={form.veterinario} onChange={set('veterinario')} /></Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancelar</Button>
        <Button variant="outlined" onClick={handleSolicitar} disabled={solicitando || !form.arancelIds?.length || !form.producto || !!form.presupuestoId} sx={{ color: TEAL, borderColor: TEAL }}>
          {solicitando ? 'Solicitando…' : (form.presupuestoNumber || 'Solicitar')}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !form.producto || !form.fecha} sx={{ bgcolor: TEAL }}>{saving ? 'Confirmando…' : 'Confirmo administración'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── MedicationActiveDialog ────────────────────────────────────────────────────
function MedicationActiveDialog({ open, onClose, patientId, patient, existing }) {
  const { currentUser, userData } = useAuth();
  const todayStr = () => new Date().toLocaleDateString('en-CA');
  const defaultVet = userData?.name || userData?.nombre || currentUser?.email || '';
  const empty = () => ({
    nombre: '', principioActivo: '', concentracion: '', cantidad: '',
    dosis: '', via: '', frecuencia: '', fecha_inicio: todayStr(), fecha_fin: '',
    diasTratamiento: '', numeroRenovaciones: '', indicaciones: '', advertencias: '', notas: '', veterinario: defaultVet,
  });
  const [meds, setMeds] = useState([empty()]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    const base = empty();
    const next = existing ? { ...base, ...existing } : base;
    if (!next.veterinario) next.veterinario = defaultVet;
    setMeds([next]);
  }, [open, existing, defaultVet]);

  const calcFin = (start, days) => {
    if (!start || !days) return '';
    const [y, m, d] = start.split('-').map(Number);
    const fin = new Date(y, m - 1, d + Number(days));
    return fin.toLocaleDateString('en-CA');
  };

  const patchMed = (i, patch) => setMeds((prev) => prev.map((m, idx) => {
    if (idx !== i) return m;
    const next = { ...m, ...patch };
    if (next.fecha_inicio && next.diasTratamiento && (patch.fecha_inicio !== undefined || patch.diasTratamiento !== undefined)) {
      next.fecha_fin = calcFin(next.fecha_inicio, Number(next.diasTratamiento));
    }
    return next;
  }));

  const setField = (i, f) => (e) => patchMed(i, { [f]: e.target.value });
  const addMed = () => setMeds((prev) => [...prev, empty()]);
  const removeMed = (i) => setMeds((prev) => prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev);

  const isValid = meds.some((m) => m.nombre && m.fecha_inicio && m.cantidad);

  const prescribedBy = {
    id: currentUser?.uid || '',
    name: userData?.name || userData?.nombre || userData?.email || '—',
    professionalId: userData?.professionalId || '',
  };

  const handleSave = async () => {
    const toSave = meds.filter((m) => m.nombre && m.fecha_inicio && m.cantidad);
    if (toSave.length === 0) return;
    setSaving(true);
    try {
      const snap = await getDoc(doc(db, 'patients', patientId));
      const list = snap.data()?.medicamentos_activos || [];
      const medWithVet = { ...toSave[0], prescribedBy };
      if (existing?._idx !== undefined) list[existing._idx] = medWithVet;
      else list.push(...toSave.map((m) => ({ ...m, prescribedBy })));
      await updateDoc(doc(db, 'patients', patientId), { medicamentos_activos: list, updatedAt: serverTimestamp() });
      onClose(true);
    } catch (e) { /* ignore */ }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, color: PRIMARY }}>{existing?._idx !== undefined ? 'Editar receta' : 'Nueva receta'}</DialogTitle>
      <DialogContent dividers sx={{ pt: 2 }}>
        {patient?.weight != null && (
          <Box sx={{ mb: 2, p: 1, bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0', display: 'flex', alignItems: 'center', gap: 1 }}>
            <FitnessCenterIcon sx={{ color: TEAL, fontSize: 18 }} />
            <Typography variant="body2" fontWeight={700} color={TEAL}>Último peso: {patient.weight} kg</Typography>
          </Box>
        )}
        <Stack spacing={3}>
          {meds.map((form, i) => (
            <Box key={i} sx={{ position: 'relative', pb: meds.length > 1 && i < meds.length - 1 ? 2 : 0, borderBottom: meds.length > 1 && i < meds.length - 1 ? '1px dashed #CBD5E1' : 'none' }}>
              {meds.length > 1 && !existing && (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" fontWeight={700} color={PRIMARY}>Medicamento {i + 1}</Typography>
                  <IconButton size="small" onClick={() => removeMed(i)} sx={{ color: DANGER }}><DeleteIcon fontSize="small" /></IconButton>
                </Box>
              )}
              <Grid container spacing={2}>
                <Grid size={12}><TextField label="Medicamento (nombre comercial) *" fullWidth size="small" value={form.nombre} onChange={setField(i, 'nombre')} /></Grid>
                <Grid size={6}><TextField label="Principio activo" fullWidth size="small" value={form.principioActivo} onChange={setField(i, 'principioActivo')} /></Grid>
                <Grid size={6}><TextField label="Concentración / presentación" fullWidth size="small" value={form.concentracion} onChange={setField(i, 'concentracion')} placeholder="Ej: 50 mg/tableta" /></Grid>
                <Grid size={12}><TextField label="Cantidad total a dispensar *" fullWidth size="small" value={form.cantidad} onChange={setField(i, 'cantidad')} placeholder="Ej: 30 tabletas" /></Grid>
                <Grid size={6}><TextField label="Dosis" fullWidth size="small" value={form.dosis} onChange={setField(i, 'dosis')} /></Grid>
                <Grid size={6}>
                  <TextField select label="Vía" fullWidth size="small" value={form.via} onChange={setField(i, 'via')}>
                    {['Oral', 'SC', 'IM', 'IV', 'Tópica', 'Oftálmica', 'Ótica', 'Inhalada', 'Rectal'].map(v => <MenuItem key={v} value={v}>{v}</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={12}><TextField label="Frecuencia" fullWidth size="small" value={form.frecuencia} onChange={setField(i, 'frecuencia')} placeholder="Ej: SID, BID, cada 12h…" /></Grid>
                <Grid size={4}><TextField label="Fecha inicio *" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.fecha_inicio} onChange={setField(i, 'fecha_inicio')} /></Grid>
                <Grid size={4}><TextField label="Días tratamiento" type="number" fullWidth size="small" value={form.diasTratamiento} onChange={setField(i, 'diasTratamiento')} placeholder="Ej: 7" /></Grid>
                <Grid size={4}><TextField label="Fecha fin" type="date" fullWidth size="small" InputLabelProps={{ shrink: true }} value={form.fecha_fin} InputProps={{ readOnly: true }} /></Grid>
                <Grid size={6}><TextField label="Número de renovaciones" fullWidth size="small" value={form.numeroRenovaciones} onChange={setField(i, 'numeroRenovaciones')} placeholder="Ej: 1" /></Grid>
                <Grid size={12}><TextField label="Indicaciones de administración" fullWidth size="small" value={form.indicaciones} onChange={setField(i, 'indicaciones')} placeholder="Ej: Con alimento" /></Grid>
                <Grid size={12}><TextField label="Advertencias / precauciones" fullWidth size="small" value={form.advertencias} onChange={setField(i, 'advertencias')} placeholder="Ej: No usar en felinos" /></Grid>
                <Grid size={12}><TextField label="Notas" fullWidth size="small" multiline rows={2} value={form.notas} onChange={setField(i, 'notas')} /></Grid>
                <Grid size={12}><TextField label="Médico veterinario" fullWidth size="small" value={form.veterinario} InputProps={{ readOnly: true }} /></Grid>
              </Grid>
            </Box>
          ))}
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addMed} sx={{ color: PRIMARY, borderColor: PRIMARY, alignSelf: 'flex-start' }}>
            Agregar medicamento
          </Button>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || !isValid} sx={{ bgcolor: PRIMARY }}>{saving ? 'Guardando…' : 'Guardar'}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── ConsultationCard ──────────────────────────────────────────────────────────
function ConsultationCard({ c, onEdit }) {
  const chip = VISIT_CHIP[c.tipoVisita] || VISIT_CHIP.consulta;
  const [exp, setExp] = useState(false);
  const dolorLabels = ['Sin dolor', 'Leve', 'Moderado', 'Severo', 'Máximo'];

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 3, overflow: 'hidden', mb: 2 }}>
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', gap: 2,
        bgcolor: exp ? '#F0F4FF' : '#FAFAFA', borderBottom: exp ? '1px solid #E2E8F0' : 'none',
        cursor: 'pointer' }} onClick={() => setExp(!exp)}>
        <Box sx={{ width: 4, height: 40, bgcolor: PRIMARY, borderRadius: 4, flexShrink: 0 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.8} alignItems="center" flexWrap="wrap">
            <Chip label={chip.label} size="small" sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 700, fontSize: '0.68rem', height: 20 }} />
            <Typography variant="body2" fontWeight={700} color={PRIMARY}>{fmtDateTime(c.createdAt)}</Typography>
            {c.bcs && <Chip label={`BCS ${c.bcs}/9`} size="small" sx={{ bgcolor: '#F0FDF4', color: SUCCESS, fontSize: '0.65rem', height: 18 }} />}
            {c.score_dolor != null && c.score_dolor !== '' && (
              <Chip label={`Dolor ${c.score_dolor}/4`} size="small"
                sx={{ bgcolor: c.score_dolor >= 3 ? '#FEE2E2' : '#FEF9C3', color: c.score_dolor >= 3 ? DANGER : WARNING, fontSize: '0.65rem', height: 18 }} />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            <strong>MC:</strong> {c.motivoConsulta || '—'}
            {c.diagnosticos?.length > 0 && ` · Dx: ${c.diagnosticos[0].descripcion}`}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="caption" color="text.secondary">{c.vetNombre || '—'}</Typography>
          {c.peso && <Typography variant="caption" display="block" color={TEAL} fontWeight={700}>{c.peso} kg</Typography>}
        </Box>
        <ExpandMoreIcon sx={{ color: 'text.secondary', transform: exp ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
      </Box>
      {exp && (
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Grid container spacing={2}>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Box sx={{ p: 2, bgcolor: '#F0F9FF', borderRadius: 2, border: '1px solid #BAE6FD', height: '100%', overflow: 'hidden' }}>
                <Typography variant="caption" fontWeight={700} color="#0369A1" display="block" mb={1}>S — SUBJETIVO</Typography>
                <Typography variant="body2" sx={{ wordBreak: 'break-word' }}><strong>Motivo:</strong> {c.motivoConsulta || '—'}</Typography>
                {c.anamnesis && <Typography variant="body2" sx={{ mt: 0.5, wordBreak: 'break-word' }}><strong>Anamnesis:</strong> {c.anamnesis}</Typography>}
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Box sx={{ p: 2, bgcolor: '#F0FDF4', borderRadius: 2, border: '1px solid #BBF7D0', height: '100%', overflow: 'hidden' }}>
                <Typography variant="caption" fontWeight={700} color="#065F46" display="block" mb={1}>O — OBJETIVO (Signos vitales)</Typography>
                <Grid container spacing={1}>
                  {[
                    ['Peso', c.peso, 'kg'], ['T°', c.temperatura, '°C'],
                    ['FC', c.frecuenciaCardiaca, 'lpm'], ['FR', c.frecuenciaRespiratoria, 'rpm'],
                    ['BCS', c.bcs, '/9'], ['SpO2', c.saturacion_oxigeno, '%'],
                    ['PA', c.presion_arterial_sistolica ? `${c.presion_arterial_sistolica}/${c.presion_arterial_diastolica}` : null, 'mmHg'],
                  ].map(([label, val, unit]) => val && (
                    <Grid key={label} size={6}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={700}>{val}{unit}</Typography>
                    </Grid>
                  ))}
                  {c.mucosas && <Grid size={6}><Typography variant="caption" color="text.secondary">Mucosas</Typography><Typography variant="body2" fontWeight={700}>{c.mucosas}</Typography></Grid>}
                  {c.score_dolor != null && c.score_dolor !== '' && (
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Dolor</Typography>
                      <Typography variant="body2" fontWeight={700} color={c.score_dolor >= 3 ? DANGER : 'text.primary'}>
                        {c.score_dolor}/4 — {dolorLabels[c.score_dolor]}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
                {c.hallazgosExamen && <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-word' }}><strong>Hallazgos:</strong> {c.hallazgosExamen}</Typography>}
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Box sx={{ p: 2, bgcolor: '#FFF7ED', borderRadius: 2, border: '1px solid #FED7AA', height: '100%', overflow: 'hidden' }}>
                <Typography variant="caption" fontWeight={700} color="#C2410C" display="block" mb={1}>A — EVALUACIÓN / DIAGNÓSTICO</Typography>
                {(c.diagnosticos || []).length > 0
                  ? <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {c.diagnosticos.map((d, i) => (
                        <Chip key={i} label={`${d.tipo === 'definitivo' ? '✓' : d.tipo === 'presuntivo' ? '?' : '±'} ${d.descripcion}`}
                          size="small" sx={{ fontSize: '0.72rem', maxWidth: '100%', height: 'auto', '& .MuiChip-label': { whiteSpace: 'normal', textOverflow: 'ellipsis', overflow: 'hidden' } }} />
                      ))}
                    </Box>
                  : <Typography variant="body2" color="text.secondary">Sin diagnóstico registrado</Typography>}
              </Box>
            </Grid>
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <Box sx={{ p: 2, bgcolor: '#FAF5FF', borderRadius: 2, border: '1px solid #E9D5FF', height: '100%', overflow: 'hidden' }}>
                <Typography variant="caption" fontWeight={700} color="#6B21A8" display="block" mb={1}>P — PLAN DE TRATAMIENTO</Typography>
                {(c.tratamientos || []).length > 0
                  ? c.tratamientos.map((t, i) => (
                    <Typography key={i} variant="body2" sx={{ mb: 0.3, wordBreak: 'break-word' }}>
                      • {t.descripcion}{t.dosis ? ` — ${t.dosis}` : ''}{t.frecuencia ? ` · ${t.frecuencia}` : ''}{t.duracion ? ` por ${t.duracion}` : ''}
                    </Typography>
                  ))
                  : <Typography variant="body2" color="text.secondary">Sin tratamiento registrado</Typography>}
                {c.instruccionesdueno && <Typography variant="body2" sx={{ mt: 1, wordBreak: 'break-word' }}><strong>Instrucciones:</strong> {c.instruccionesdueno}</Typography>}
                {c.proximoControl && (
                  <Typography variant="body2" color={TEAL} fontWeight={600} sx={{ mt: 0.5 }}>
                    Próximo control: {fmtDate(c.proximoControl)}
                  </Typography>
                )}
              </Box>
            </Grid>
          </Grid>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(c)} sx={{ color: PRIMARY }}>Editar consulta</Button>
          </Box>
        </Box>
      )}
    </Paper>
  );
}

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

const TIPOS_MUERTE = [
  { key: 'NATURAL_EN_CLINICA', label: 'Natural en clínica' },
  { key: 'DURANTE_PROCEDIMIENTO_ANESTESIA', label: 'Durante procedimiento / anestesia' },
  { key: 'EUTANASIA', label: 'Eutanasia' },
  { key: 'FUERA_DE_CLINICA', label: 'Fuera de la clínica' },
  { key: 'DOA', label: 'Llegó sin vida (DOA)' },
  { key: 'DESCONOCIDA', label: 'Causa desconocida' },
];
const LUGARES_MUERTE = [
  { key: 'CLINICA', label: 'Clínica' },
  { key: 'DOMICILIO', label: 'Domicilio del tutor' },
  { key: 'OTRA_CLINICA', label: 'Otra clínica' },
  { key: 'OTRO', label: 'Otro' },
];
const DISPOSICIONES = [
  { key: 'PENDIENTE', label: 'Pendiente' },
  { key: 'ENTREGA_TUTOR', label: 'Entrega del cuerpo al tutor' },
  { key: 'CREMACION_INDIVIDUAL', label: 'Cremación individual' },
  { key: 'CREMACION_COLECTIVA', label: 'Cremación colectiva' },
  { key: 'ENTIERRO', label: 'Entierro' },
  { key: 'NECROPSIA_LUEGO_CREMACION', label: 'Necropsia y luego cremación' },
];

function toDateTimeLocal(d) {
  const date = d ? (typeof d.toDate === 'function' ? d.toDate() : new Date(d)) : new Date();
  const pad = (n) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function AnularDefuncionDialog({ open, onClose, patientId, onSaved }) {
  const { currentUser, userData } = useAuth();
  const [saving, setSaving] = useState(false);
  const [motivo, setMotivo] = useState('');

  useEffect(() => {
    if (open) setMotivo('');
  }, [open]);

  const handleConfirm = async () => {
    if (!motivo.trim()) { alert('Debe indicar el motivo de la anulación.'); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'patients', patientId), {
        estado_vital: 'VIVO',
        estado_ficha: 'ACTIVA',
        defuncion_anulada: {
          anulada_por: userData?.name || currentUser?.email || '',
          anulada_por_id: currentUser?.uid || '',
          anulada_en: serverTimestamp(),
          motivo: motivo.trim(),
        },
        updatedAt: serverTimestamp(),
      });
      onSaved();
      onClose();
    } catch (e) {
      console.error('Error al anular defunción:', e);
      alert('Error al anular: ' + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: '#7f1d1d', fontWeight: 700 }}>Anular defunción</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
            Esta acción reabre la ficha del paciente. Las citas y recordatorios cancelados por la defunción no se reactivan automáticamente.
          </Alert>
          <TextField
            label="Motivo de la anulación"
            multiline
            rows={3}
            size="small"
            value={motivo}
            onChange={e => setMotivo(e.target.value)}
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="error" onClick={handleConfirm} disabled={saving || !motivo.trim()}>
          {saving ? 'Guardando…' : 'Confirmar anulación'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function DeceasedRegistrationDialog({ open, onClose, patientId, onSaved }) {
  const { currentUser, userData } = useAuth();
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    fechaHoraDefuncion: toDateTimeLocal(),
    tipoMuerte: '',
    lugar: 'CLINICA',
    causaPrincipal: '',
    disposicionRestos: 'PENDIENTE',
    fechaEsAproximada: false,
    notas: '',
  });

  useEffect(() => {
    if (open) setForm({
      fechaHoraDefuncion: toDateTimeLocal(),
      tipoMuerte: '',
      lugar: 'CLINICA',
      causaPrincipal: '',
      disposicionRestos: 'PENDIENTE',
      fechaEsAproximada: false,
      notas: '',
    });
  }, [open]);

  const f = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    if (!form.tipoMuerte) { alert('Debe seleccionar el tipo de muerte.'); return; }
    const selected = new Date(form.fechaHoraDefuncion);
    if (isNaN(selected.getTime()) || selected > new Date()) { alert('La fecha/hora de defunción no puede ser futura o inválida.'); return; }
    setSaving(true);
    try {
      await updateDoc(doc(db, 'patients', patientId), {
        estado_vital: 'FALLECIDO',
        estado_ficha: 'CERRADA',
        fecha_hora_defuncion: selected,
        fecha_es_aproximada: form.fechaEsAproximada,
        tipo_muerte: form.tipoMuerte,
        lugar: form.lugar,
        causa_principal: form.causaPrincipal?.trim() || '',
        disposicion_restos: form.disposicionRestos,
        notas_defuncion: form.notas?.trim() || '',
        veterinario_certificante_id: currentUser?.uid || '',
        veterinario_certificante_name: userData?.name || currentUser?.email || '',
        registrado_por: userData?.name || currentUser?.email || '',
        registrado_por_id: currentUser?.uid || '',
        registrado_en: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      // Cancelar citas futuras
      const today = new Date(); today.setHours(0,0,0,0);
      const [b1, b2] = await Promise.all([
        getDocs(query(collection(db, 'onlineBookings'), where('patientId', '==', patientId))),
        getDocs(query(collection(db, 'onlineBookings'), where('petId', '==', patientId))),
      ]);
      const bookings = new Map();
      [b1, b2].forEach(snap => snap.docs.forEach(d => bookings.set(d.id, { id: d.id, ...d.data() })));
      for (const b of bookings.values()) {
        const bDate = b.date ? new Date(b.date + 'T00:00:00') : null;
        if (bDate && bDate >= today && b.status !== 'cancelled' && b.status !== 'no_show') {
          await updateDoc(doc(db, 'onlineBookings', b.id), {
            status: 'cancelled',
            cancellationReason: 'Paciente fallecido',
            cancelledAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
      }

      // Cerrar hospitalizaciones activas
      const hSnap = await getDocs(query(collection(db, 'hospitalizations'), where('patientId', '==', patientId)));
      for (const h of hSnap.docs) {
        const data = h.data();
        if (data.status !== 'alta') {
          await updateDoc(doc(db, 'hospitalizations', h.id), {
            status: 'alta',
            egresoTipo: 'fallecido',
            altaAt: selected,
            altaBy: userData?.name || currentUser?.email || '',
            updatedAt: serverTimestamp(),
          });
        }
      }

      onSaved();
      onClose();
    } catch (e) {
      console.error('Error al registrar defunción:', e);
      alert('Error al registrar: ' + (e?.message || e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ color: '#7f1d1d', fontWeight: 700 }}>Registrar fallecimiento</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="warning" sx={{ fontSize: '0.8rem' }}>
            Esta acción cierra la ficha del paciente. Solo un administrador podrá anularla.
          </Alert>
          <TextField
            label="Fecha y hora de defunción"
            type="datetime-local"
            size="small"
            value={form.fechaHoraDefuncion}
            onChange={e => f('fechaHoraDefuncion', e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <FormControlLabel
            control={<Checkbox checked={form.fechaEsAproximada} onChange={e => f('fechaEsAproximada', e.target.checked)} />}
            label="La fecha/hora es aproximada"
          />
          <TextField select size="small" label="Tipo de muerte" value={form.tipoMuerte} onChange={e => f('tipoMuerte', e.target.value)} fullWidth>
            {TIPOS_MUERTE.map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Lugar" value={form.lugar} onChange={e => f('lugar', e.target.value)} fullWidth>
            {LUGARES_MUERTE.map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
          </TextField>
          <TextField select size="small" label="Disposición de restos" value={form.disposicionRestos} onChange={e => f('disposicionRestos', e.target.value)} fullWidth>
            {DISPOSICIONES.map(t => <MenuItem key={t.key} value={t.key}>{t.label}</MenuItem>)}
          </TextField>
          <TextField size="small" label="Causa principal" value={form.causaPrincipal} onChange={e => f('causaPrincipal', e.target.value)} fullWidth />
          <TextField size="small" label="Notas" multiline rows={2} value={form.notas} onChange={e => f('notas', e.target.value)} fullWidth />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancelar</Button>
        <Button variant="contained" color="error" onClick={handleSave} disabled={saving}>
          {saving ? 'Guardando…' : 'Confirmar fallecimiento'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Componente principal ─────────────────────────────────────────────────────
export default function PatientRecord() {
  const { patientId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser, userData } = useAuth();
  const { activeBranch, filterByBranch } = useBranch();
  const isVet = ['médico veterinario', 'director médico'].includes((userData?.role || '').toLowerCase());
  const isAdmin = userData?.role === 'admin';

  const [patient, setPatient]         = useState(null);
  const [owner, setOwner]             = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [weights, setWeights]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [tab, setTab]                 = useState(0);
  const [copied, setCopied]           = useState(false);

  const [profileDlg, setProfileDlg]   = useState(false);
  const [vaccineDlg, setVaccineDlg]   = useState({ open: false, existing: null });
  const [antiDlg, setAntiDlg]         = useState({ open: false, existing: null });
  const [medDlg, setMedDlg]           = useState({ open: false, existing: null });

  const [specialties, setSpecialties] = useState([]);
  const [interForm, setInterForm]     = useState({ specialtyIds: [], notas: '', examenes: '' });
  const [savingInter, setSavingInter] = useState(false);
  const [hospitalizations, setHospitalizations] = useState([]);
  const [pendingHospOrders, setPendingHospOrders] = useState([]);
  const [surgeryProtocols, setSurgeryProtocols] = useState([]);
  const [surgeryRequests, setSurgeryRequests] = useState([]);
  const [archivos, setArchivos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [deceasedDlg, setDeceasedDlg] = useState(false);
  const [anularDlg, setAnularDlg] = useState(false);

  const loadPatient = useCallback(async () => {
    try {
      const snap = await getDoc(doc(db, 'patients', patientId));
      if (!snap.exists()) return;
      const p = { id: snap.id, ...snap.data() };
      setPatient(p);
      if (p.ownerId) {
        const oSnap = await getDoc(doc(db, 'owners', p.ownerId));
        if (oSnap.exists()) setOwner({ id: oSnap.id, ...oSnap.data() });
      }
    } catch (_) {}
    setLoading(false);
  }, [patientId]);

  useEffect(() => { loadPatient(); }, [loadPatient]);

  useEffect(() => {
    const q = query(collection(db, 'clinicalRecords'), where('patientId', '==', patientId));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort((a, b) => {
        const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return tb - ta;
      });
      setConsultations(filterByBranch(rows, { includeUnassigned: true }));
    });
  }, [patientId]);

  useEffect(() => {
    const q = query(collection(db, 'patients', patientId, 'weights'), orderBy('date', 'desc'));
    return onSnapshot(q, (snap) => setWeights(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, [patientId]);

  useEffect(() => {
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, 'specialties'));
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((s) => s.active !== false);
        setSpecialties(list.sort((a, b) => (a.name || '').localeCompare(b.name || '')));
      } catch (_) {}
    };
    load();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'hospitalizations'), where('patientId', '==', patientId));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => {
        const ta = a.admissionAt?.toMillis ? a.admissionAt.toMillis() : new Date(a.admissionAt || 0).getTime();
        const tb = b.admissionAt?.toMillis ? b.admissionAt.toMillis() : new Date(b.admissionAt || 0).getTime();
        return tb - ta;
      });
      setHospitalizations(filterByBranch(rows, { includeUnassigned: true }));
    });
  }, [patientId]);

  useEffect(() => {
    const q = query(collection(db, 'hospitalizationOrders'), where('patientId', '==', patientId), where('status', '==', 'pendiente'));
    return onSnapshot(q, (snap) => {
      setPendingHospOrders(filterByBranch(snap.docs.map((d) => ({ id: d.id, ...d.data() })), { includeUnassigned: true }));
    });
  }, [patientId]);

  useEffect(() => {
    const q = query(collection(db, 'surgeryProtocols'), where('patientId', '==', patientId));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
      setSurgeryProtocols(filterByBranch(rows, { includeUnassigned: true }));
    });
  }, [patientId]);

  useEffect(() => {
    const q = query(collection(db, 'surgeryRequests'), where('patientId', '==', patientId));
    return onSnapshot(q, (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() })).sort((a, b) => {
        const ta = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
        const tb = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
        return tb - ta;
      });
      setSurgeryRequests(filterByBranch(rows, { includeUnassigned: true }));
    });
  }, [patientId]);

  useEffect(() => {
    const q = query(collection(db, 'patients', patientId, 'archivos'), orderBy('uploadedAt', 'desc'));
    return onSnapshot(q, (snap) => {
      setArchivos(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });
  }, [patientId]);

  const handleDeleteVaccine = async (idx) => {
    const vaccines = [...(patient.vacunas || [])];
    vaccines.splice(idx, 1);
    await updateDoc(doc(db, 'patients', patientId), { vacunas: vaccines, updatedAt: serverTimestamp() });
    loadPatient();
  };

  const handleDeleteAnti = async (idx) => {
    const list = [...(patient.antiparasitarios || [])]; list.splice(idx, 1);
    await updateDoc(doc(db, 'patients', patientId), { antiparasitarios: list, updatedAt: serverTimestamp() });
    loadPatient();
  };

  const handleDiscontinueMed = async (idx) => {
    const list = [...(patient.medicamentos_activos || [])]; list.splice(idx, 1);
    await updateDoc(doc(db, 'patients', patientId), { medicamentos_activos: list, updatedAt: serverTimestamp() });
    loadPatient();
  };

  const toggleInterSpecialty = (id) => (e) => {
    const checked = e.target.checked;
    setInterForm((p) => ({
      ...p,
      specialtyIds: checked ? [...p.specialtyIds, id] : p.specialtyIds.filter((s) => s !== id),
    }));
  };

  const handleSaveInter = async () => {
    if (interForm.specialtyIds.length === 0 && !interForm.notas.trim() && !interForm.examenes.trim()) return;
    setSavingInter(true);
    try {
      const snap = await getDoc(doc(db, 'patients', patientId));
      const list = snap.data()?.interconsultas || [];
      const specialtyNames = specialties.filter((s) => interForm.specialtyIds.includes(s.id)).map((s) => s.name);
      const newItem = {
        id: `ic-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        specialtyIds: interForm.specialtyIds,
        specialtyNames,
        notas: interForm.notas,
        examenes: interForm.examenes,
        fecha: new Date().toLocaleDateString('es-CL'),
        veterinario: userData?.name || userData?.email || '',
        veterinarioId: userData?.professionalId || currentUser?.uid || '',
        createdAt: new Date().toISOString(),
      };
      list.push(newItem);
      await updateDoc(doc(db, 'patients', patientId), { interconsultas: list, updatedAt: serverTimestamp() });
      setInterForm({ specialtyIds: [], notas: '', examenes: '' });
      loadPatient();
    } catch (e) { console.error('Error al guardar interconsulta:', e); alert('Error al guardar: ' + (e?.message || e)); }
    setSavingInter(false);
  };

  const handleDeleteInter = async (idx) => {
    const list = [...(patient.interconsultas || [])]; list.splice(idx, 1);
    await updateDoc(doc(db, 'patients', patientId), { interconsultas: list, updatedAt: serverTimestamp() });
    loadPatient();
  };

  const printInter = async (ic) => {
    try {
      const doc = await generateInterconsultaPDF(ic, patient, owner, activeBranch || userData?.branch || {}, userData?.name || userData?.email);
      doc.save(`Interconsulta_${patient?.name || ''}_${ic.id}.pdf`);
    } catch (e) { console.error('Error al imprimir interconsulta:', e); alert('Error al generar PDF'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
    if (!validTypes.includes(file.type)) {
      alert('Solo se permiten imágenes JPG/PNG/WebP o PDF.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('El archivo excede 10 MB.');
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    try {
      const ext = file.name.split('.').pop();
      const safeName = `${Date.now()}_${Math.random().toString(36).slice(2)}.${ext}`;
      const storageRef = ref(storage, `patients/${patientId}/${safeName}`);
      const uploadTask = uploadBytesResumable(storageRef, file);
      uploadTask.on('state_changed',
        (snapshot) => setUploadProgress(Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)),
        (err) => { console.error(err); alert('Error al subir archivo'); setUploading(false); },
        async () => {
          const url = await getDownloadURL(uploadTask.snapshot.ref);
          await addDoc(collection(db, 'patients', patientId, 'archivos'), {
            name: file.name,
            storagePath: `patients/${patientId}/${safeName}`,
            url,
            type: file.type,
            size: file.size,
            uploadedAt: serverTimestamp(),
            uploadedBy: userData?.name || userData?.email || 'Usuario',
          });
          setUploading(false);
          setUploadProgress(0);
          e.target.value = '';
        }
      );
    } catch (err) {
      console.error(err);
      alert('Error al subir archivo');
      setUploading(false);
    }
  };

  const handleDeleteArchivo = async (a) => {
    if (!window.confirm('¿Eliminar este archivo?')) return;
    try {
      await deleteObject(ref(storage, a.storagePath));
      await deleteDoc(doc(db, 'patients', patientId, 'archivos', a.id));
    } catch (err) {
      console.error(err);
      alert('Error al eliminar archivo');
    }
  };

  const weightTrend = useMemo(() => pesoTendencia(weights), [weights]);
  const latestWeight = weights[0]?.weight;
  const activeHosp = useMemo(() => hospitalizations.find((h) => h.status !== 'alta'), [hospitalizations]);
  const lastDischarge = useMemo(() => {
    const altas = hospitalizations.filter((h) => h.status === 'alta' && h.altaDiagnosticoEgreso);
    altas.sort((a, b) => {
      const ta = new Date(a.altaAt || a.admissionAt || 0).getTime();
      const tb = new Date(b.altaAt || b.admissionAt || 0).getTime();
      return tb - ta;
    });
    return altas[0] || null;
  }, [hospitalizations]);

  const printPrescription = async (meds) => {
    const list = Array.isArray(meds) ? meds : (meds ? [meds] : []);
    if (list.length === 0) return;
    const branch = normalizeBranch(activeBranch);
    const date = new Date().toLocaleDateString('es-CL');
    const vetName = list[0]?.veterinario || list[0]?.prescribedBy?.name || userData?.name || userData?.nombre || userData?.email || '—';
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const logos = await loadLogos();
    renderStandardHeader(doc, branch, logos, { title: '', number: '', date });
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.setTextColor(...COLORS_PDF.primary);
    const pw = doc.internal.pageSize.getWidth();
    doc.text('RECETA MÉDICA VETERINARIA', pw / 2, 23, { align: 'center' });

    const margin = 16;
    const cw = pw - margin * 2;
    let y = 35;

    const ensureSpace = (h = 40) => {
      const pageH = doc.internal.pageSize.getHeight();
      if (y + h > pageH - 30) {
        doc.addPage();
        renderStandardHeader(doc, branch, logos, { title: '', number: '', date });
        y = 35;
      }
      return y;
    };

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS_PDF.primary);
    doc.text('Paciente', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS_PDF.text);
    doc.text(`${patient.name || '—'}  ·  ${patient.species || '—'}  ·  ${patient.breed || ''}  ·  Peso: ${latestWeight != null ? latestWeight + ' kg' : '—'}`, margin, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS_PDF.primary);
    doc.text('Tutor', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS_PDF.text);
    const tutor = [owner?.name, owner?.rut ? `RUT: ${owner.rut}` : '', owner?.celular ? `Tel: ${owner.celular}` : ''].filter(Boolean).join('  ·  ');
    doc.text(tutor || '—', margin, y);
    y += 8;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...COLORS_PDF.primary);
    doc.text('Médico veterinario', margin, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS_PDF.text);
    doc.text(vetName, margin, y);
    y += 10;

    ensureSpace(10);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(...COLORS_PDF.primary);
    doc.text('Rp.', margin, y);
    y += 10;

    list.forEach((med) => {
      ensureSpace(70);

      const generic = med.principioActivo || med.nombre || '—';
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.setTextColor(...COLORS_PDF.text);
      const nameLines = doc.splitTextToSize(generic, cw);
      doc.text(nameLines, margin, y);
      y += nameLines.length * 5.5 + 1;

      if (med.nombre && med.principioActivo && med.nombre !== med.principioActivo) {
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(...COLORS_PDF.muted);
        const rec = `Recomendado: ${med.nombre}`;
        const recLines = doc.splitTextToSize(rec, cw);
        doc.text(recLines, margin, y);
        y += recLines.length * 4.5 + 1.5;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.setTextColor(...COLORS_PDF.text);
      doc.text('Aplicación:', margin, y);
      y += 4.5;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      const dose = [
        med.dosis || '',
        med.via ? `vía ${med.via}` : '',
        med.frecuencia ? `cada ${med.frecuencia}` : '',
        med.diasTratamiento ? `por ${med.diasTratamiento} días` : (med.fecha_fin ? `hasta ${med.fecha_fin}` : ''),
      ].filter(Boolean).join('  ·  ') || '—';
      const appLines = doc.splitTextToSize(dose, cw);
      doc.text(appLines, margin + 3, y);
      y += appLines.length * 4.5 + 2;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Inicio del Tratamiento:', margin, y);
      y += 4.5;
      doc.setFont('helvetica', 'normal');
      const start = med.fecha_inicio ? new Date(med.fecha_inicio + 'T00:00:00').toLocaleDateString('es-CL') : '—';
      const startLines = doc.splitTextToSize(start, cw);
      doc.text(startLines, margin + 3, y);
      y += startLines.length * 4.5 + 2;

      const obs = [med.indicaciones, med.advertencias, med.notas].filter(Boolean).join('  ·  ');
      if (obs) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9);
        doc.text('Observaciones:', margin, y);
        y += 4.5;
        doc.setFont('helvetica', 'normal');
        const obsLines = doc.splitTextToSize(obs, cw);
        doc.text(obsLines, margin + 3, y);
        y += obsLines.length * 4.5 + 2;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('Cantidad:', margin, y);
      y += 4.5;
      doc.setFont('helvetica', 'normal');
      const qtyLines = doc.splitTextToSize(med.cantidad || '—', cw);
      doc.text(qtyLines, margin + 3, y);
      y += qtyLines.length * 4.5 + 5;
    });

    ensureSpace(25);
    const pageH = doc.internal.pageSize.getHeight();
    const firmaY = Math.min(y + 6, pageH - 60);
    doc.setDrawColor(...COLORS_PDF.muted);
    doc.setLineWidth(0.3);
    doc.line(margin, firmaY, margin + 70, firmaY);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...COLORS_PDF.primary);
    doc.text(`Médico veterinario: ${vetName}`, margin, firmaY + 6);

    renderStandardFooter(doc, branch);
    doc.autoPrint();
    doc.output('dataurlnewwindow');
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  if (!patient) return <Alert severity="error" sx={{ m: 3 }}>Paciente no encontrado.</Alert>;

  const isDeceased = patient?.estado_vital === 'FALLECIDO';
  const sc = (patient.species || '').toLowerCase();
  const avatarBg = sc === 'canino' ? '#FF9800' : sc === 'felino' ? '#AB47BC' : '#42A5F5';
  const ownerEmail = owner?.mail || owner?.contact || '';

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const medStatus = (m) => {
    if (!m.fecha_fin) return { label: 'En curso',     bg: '#DCFCE7', color: SUCCESS };
    const fin = new Date(m.fecha_fin);
    const diff = Math.floor((fin - today) / 86400000);
    if (diff < 0)  return { label: 'Vencido',          bg: '#FEE2E2', color: DANGER };
    if (diff <= 3) return { label: 'Vence pronto',     bg: '#FEF9C3', color: WARNING };
    return              { label: 'En curso',           bg: '#DCFCE7', color: SUCCESS };
  };

  return (
    <Box sx={{ bgcolor: '#F4F6F8', minHeight: '100vh', p: { xs: 1.5, md: 3 }, overflowX: 'hidden', maxWidth: '100%', width: '100%', boxSizing: 'border-box' }}>
      {/* ══ HEADER ENRIQUECIDO ══════════════════════════════════════════════ */}
      <Paper elevation={0} sx={{ p: { xs: 2, md: 2.5 }, borderRadius: 3, border: '1px solid #E2E8F0', mb: 2 }}>
        <Grid container spacing={2} alignItems="flex-start">

          {/* Col 1 — Identidad */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
              <Avatar src={patient.photoURL || undefined} sx={{ bgcolor: avatarBg, width: 72, height: 72, mb: 1.5, fontSize: 28, fontWeight: 700 }}>
                {!patient.photoURL && (patient.name?.[0]?.toUpperCase() || <PetsIcon />)}
              </Avatar>
              <Typography variant="h6" fontWeight={800} color={PRIMARY}>{patient.name}</Typography>
              <Typography variant="body2" color="text.secondary">{patient.species} · {patient.breed || '—'}</Typography>
              <Stack direction="row" spacing={0.8} mt={1} flexWrap="wrap" justifyContent="center">
                <Chip label={patient.genero || '—'} size="small" sx={{ fontSize: '0.7rem' }} />
                <Chip label={calcAge(patient.birthDate)} size="small" sx={{ bgcolor: '#DBEAFE', color: '#1D4ED8', fontSize: '0.7rem' }} />
                {latestWeight != null && <Chip label={`Peso ${latestWeight} kg`} size="small" sx={{ bgcolor: '#F0FDF4', color: TEAL, fontSize: '0.7rem', fontWeight: 700 }} />}
                {owner?.convenio && <Chip label={owner.convenio} size="small" sx={{ bgcolor: '#ECFDF5', color: '#15803D', fontSize: '0.7rem', fontWeight: 700 }} />}
                {(patient.status === 'hospitalizado' || activeHosp) && <Chip label="Hospitalizado" size="small" sx={{ bgcolor: '#FEF9C3', color: WARNING, fontSize: '0.7rem', fontWeight: 700 }} />}
                {isDeceased && (
                  <Chip label="Fallecido" size="small" sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontSize: '0.7rem', fontWeight: 700 }} />
                )}
              </Stack>
              {patient.microchip && (
                <Tooltip title={copied ? '¡Copiado!' : 'Copiar microchip'}>
                  <Chip icon={<ContentCopyIcon sx={{ fontSize: '13px !important' }} />} label={patient.microchip} size="small" variant="outlined"
                    onClick={() => { navigator.clipboard.writeText(patient.microchip); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                    sx={{ mt: 1, fontSize: '0.68rem', cursor: 'pointer', maxWidth: 200 }} />
                </Tooltip>
              )}
            </Box>
          </Grid>

          {/* Col 2 — Alertas + vitales recientes */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <ActiveAlertsPanel patient={patient} />
            {consultations.length > 0 && (
              <Grid container spacing={1}>
                {weightTrend && (
                  <Grid
                    size={{
                      xs: 6,
                      sm: 3
                    }}>
                    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: '#F0FDF4', border: '1px solid #BBF7D0', textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary" display="block">Peso</Typography>
                      <Typography variant="subtitle2" fontWeight={800} color={TEAL}>{weightTrend.actual} kg</Typography>
                      {weightTrend.diff && (
                        <Typography variant="caption" sx={{ color: weightTrend.color, fontWeight: 700 }}>
                          {weightTrend.icono} {Math.abs(parseFloat(weightTrend.diff)).toFixed(1)} kg
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                )}
                {(() => {
                  const last = consultations[0];
                  return [
                    last?.temperatura        && ['T°',  `${last.temperatura}°C`,              '#FFF7ED', '#FED7AA'],
                    last?.frecuenciaCardiaca && ['FC',  `${last.frecuenciaCardiaca} lpm`,      '#EFF6FF', '#BFDBFE'],
                    last?.bcs               && ['BCS', `${last.bcs}/9`,                       '#F0FDF4', '#BBF7D0'],
                  ].filter(Boolean).map(([label, val, bg, border]) => (
                    <Grid
                      key={label}
                      size={{
                        xs: 6,
                        sm: 3
                      }}>
                      <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, bgcolor: bg, border: `1px solid ${border}`, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.secondary" display="block">{label}</Typography>
                        <Typography variant="subtitle2" fontWeight={800}>{val}</Typography>
                      </Paper>
                    </Grid>
                  ));
                })()}
              </Grid>
            )}
          </Grid>

          {/* Col 3 — Acciones + propietario */}
          <Grid
            size={{
              xs: 12,
              md: 3
            }}>
            <Stack spacing={1}>
              {isVet && !isDeceased && (
                <Button variant="contained" startIcon={<AddIcon />} fullWidth
                  onClick={() => navigate(`/patients/${patientId}/consultation`)}
                  sx={{ bgcolor: PRIMARY, fontWeight: 700, borderRadius: 2 }}>
                  Nueva consulta
                </Button>
              )}
              <Button variant="outlined" startIcon={<RequestQuoteIcon />} fullWidth
                onClick={() => navigate(`/presupuestos/nuevo?patientId=${patientId}`)}
                sx={{ borderColor: '#CBD5E1', color: PRIMARY, borderRadius: 2 }}>
                Crear presupuesto
              </Button>
              {patient.ownerId && (
                <Button variant="outlined" startIcon={<AccountBalanceWalletIcon />} fullWidth
                  onClick={() => navigate(`/cuenta/${patient.ownerId}`)}
                  sx={{ borderColor: '#CBD5E1', color: PRIMARY, borderRadius: 2 }}>
                  Ver cuenta
                </Button>
              )}
              <Button variant="outlined" startIcon={<PrintIcon />} fullWidth onClick={() => window.print()}
                sx={{ borderColor: '#CBD5E1', color: PRIMARY, borderRadius: 2 }}>
                Imprimir ficha
              </Button>
              {isVet && !isDeceased && (
                <Button variant="outlined" color="error" startIcon={<WarningAmberIcon />} fullWidth onClick={() => setDeceasedDlg(true)}
                  sx={{ borderColor: '#FECACA', color: '#991B1B', borderRadius: 2 }}>
                  Marcar fallecido
                </Button>
              )}
              {isDeceased && isAdmin && (
                <Button variant="outlined" color="error" startIcon={<WarningAmberIcon />} fullWidth onClick={() => setAnularDlg(true)}
                  sx={{ borderColor: '#FECACA', color: '#991B1B', borderRadius: 2 }}>
                  Anular defunción
                </Button>
              )}
              <Button variant="text" startIcon={<ArrowBackIcon />} fullWidth
                onClick={() => {
                  const returnTo = searchParams.get('returnTo');
                  navigate(returnTo ? decodeURIComponent(returnTo) : '/patients');
                }}
                sx={{ color: 'text.secondary', borderRadius: 2 }}>
                {searchParams.get('returnTo') ? 'Volver' : 'Volver a pacientes'}
              </Button>
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="caption" color="text.secondary" fontWeight={700} display="block">PROPIETARIO</Typography>
            <Typography variant="body2" fontWeight={700}>{owner?.name || '—'}</Typography>
            {owner?.celular   && <Typography variant="caption" color="text.secondary" display="block">📞 {owner.celular}</Typography>}
            {ownerEmail       && <Typography variant="caption" color="text.secondary" display="block">✉ {ownerEmail}</Typography>}
          </Grid>
        </Grid>
      </Paper>
      {/* ══ TABS ═══════════════════════════════════════════════════════════ */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E2E8F0', overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ borderBottom: '2px solid #E2E8F0',
            '& .MuiTab-root': { fontWeight: 600, textTransform: 'none', minHeight: 48 },
            '& .Mui-selected': { color: PRIMARY },
            '& .MuiTabs-indicator': { bgcolor: TEAL } }}>
          <Tab label="Resumen" />
          <Tab label={`Historial (${consultations.length})`} />
          <Tab label="Recetas" />
          <Tab label="Preventivos" />
          <Tab label="Exámenes" />
          <Tab label="Cirugía" />
          <Tab label="Hospital" />
          <Tab label="Indicaciones" />
          <Tab label="Archivos" />
        </Tabs>

        <Box sx={{ p: { xs: 1.5, md: 2.5 } }}>

          {/* ── TAB 0: RESUMEN ───────────────────────────────────────────── */}
          <TabPanel value={tab} index={0}>
            <Grid container spacing={2.5}>
              {/* Sidebar */}
              <Grid
                size={{
                  xs: 12,
                  md: 4
                }}>
                {/* Perfil médico */}
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight={700} color={PRIMARY}>Perfil médico</Typography>
                    <Tooltip title="Editar"><IconButton size="small" onClick={() => setProfileDlg(true)} sx={{ color: PRIMARY }}><EditIcon sx={{ fontSize: 16 }} /></IconButton></Tooltip>
                  </Box>
                  {[
                    ['Grupo sanguíneo', patient.grupoSanguineo],
                    ['Esterilizado/a', patient.esterilizado === true ? 'Sí' : patient.esterilizado === false ? 'No' : null],
                    ['Esterilización', patient.esterilizacionFecha],
                    ['Peso ideal', patient.peso_objetivo ? `${patient.peso_objetivo} kg` : null],
                    ['Entorno', patient.entorno],
                    ['Dieta', patient.dieta?.marca ? `${patient.dieta.marca}${patient.dieta.tipo ? ` (${patient.dieta.tipo})` : ''}` : null],
                  ].map(([label, val]) => val ? (
                    <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.4 }}>
                      <Typography variant="caption" color="text.secondary">{label}</Typography>
                      <Typography variant="caption" fontWeight={600}>{val}</Typography>
                    </Box>
                  ) : null)}
                  {patient.alergias && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: '#FEF2F2', borderRadius: 1.5, border: '1px solid #FECACA' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.3 }}>
                        <WarningAmberIcon sx={{ fontSize: 14, color: DANGER }} />
                        <Typography variant="caption" fontWeight={700} color={DANGER}>Alergias</Typography>
                      </Box>
                      <Typography variant="caption">{patient.alergias}</Typography>
                    </Box>
                  )}
                  {patient.condicionesCronicas && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: '#FFF7ED', borderRadius: 1.5, border: '1px solid #FED7AA' }}>
                      <Typography variant="caption" fontWeight={700} color="#C2410C" display="block">Condiciones crónicas</Typography>
                      <Typography variant="caption">{patient.condicionesCronicas}</Typography>
                    </Box>
                  )}
                  {patient.notasMedicas && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: '#F8FAFC', borderRadius: 1.5, border: '1px solid #E2E8F0' }}>
                      <Typography variant="caption" fontWeight={700} display="block">Notas</Typography>
                      <Typography variant="caption" sx={{ wordBreak: 'break-word' }}>{patient.notasMedicas}</Typography>
                    </Box>
                  )}
                  {lastDischarge && (
                    <Box sx={{ mt: 1, p: 1, bgcolor: '#ECFDF5', borderRadius: 1.5, border: '1px solid #A7F3D0' }}>
                      <Typography variant="caption" fontWeight={700} color="#065F46" display="block">Diagnóstico de alta</Typography>
                      <Typography variant="caption">{lastDischarge.altaDiagnosticoEgreso}</Typography>
                      {lastDischarge.altaAt && (
                        <Typography variant="caption" color="text.secondary" display="block">{fmtDate(lastDischarge.altaAt)}</Typography>
                      )}
                    </Box>
                  )}
                </Paper>

                {/* Evolución de peso */}
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                    <FitnessCenterIcon sx={{ color: TEAL, fontSize: 16 }} />
                    <Typography variant="subtitle2" fontWeight={700} color={PRIMARY}>Evolución de peso</Typography>
                  </Box>
                  <WeightChart weights={weights} pesoIdeal={patient.peso_objetivo ? parseFloat(patient.peso_objetivo) : undefined} />
                </Paper>

              </Grid>

              {/* Área principal: últimas 3 consultas */}
              <Grid
                size={{
                  xs: 12,
                  md: 8
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Últimas consultas</Typography>
                  {consultations.length > 3 && (
                    <Button size="small" onClick={() => setTab(1)} sx={{ color: PRIMARY }}>Ver todo el historial ({consultations.length}) →</Button>
                  )}
                </Box>
                {consultations.length === 0 ? (
                  <Paper elevation={0} sx={{ p: 5, borderRadius: 3, border: '2px dashed #E2E8F0', textAlign: 'center' }}>
                    <MedicalServicesIcon sx={{ fontSize: 44, color: '#CBD5E1', mb: 1 }} />
                    <Typography variant="body1" color="text.secondary" fontWeight={600}>Sin consultas registradas</Typography>
                    <Typography variant="caption" color="text.disabled">Registra la primera consulta con el botón "Nueva consulta"</Typography>
                  </Paper>
                ) : (
                  consultations.slice(0, 3).map((c) => (
                    <ConsultationCard key={c.id} c={c} onEdit={(cons) => navigate(`/patients/${patientId}/consultation/${cons.id}`)} />
                  ))
                )}
              </Grid>
            </Grid>
          </TabPanel>

          {/* ── TAB 1: HISTORIAL ─────────────────────────────────────────── */}
          <TabPanel value={tab} index={1}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Historial de consultas</Typography>
              <Chip label={consultations.length} size="small" sx={{ ml: 1, bgcolor: '#DBEAFE', color: '#1D4ED8', fontWeight: 700 }} />
            </Box>
            {consultations.length === 0 ? (
              <Paper elevation={0} sx={{ p: 6, borderRadius: 3, border: '2px dashed #E2E8F0', textAlign: 'center' }}>
                <MedicalServicesIcon sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
                <Typography variant="body1" color="text.secondary" fontWeight={600}>Sin consultas registradas</Typography>
              </Paper>
            ) : (
              consultations.map((c) => (
                <ConsultationCard key={c.id} c={c} onEdit={(cons) => navigate(`/patients/${patientId}/consultation/${cons.id}`)} />
              ))
            )}
          </TabPanel>

          {/* ── TAB 2: MEDICAMENTOS ──────────────────────────────────────── */}
          <TabPanel value={tab} index={2}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Medicamentos activos</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="outlined" startIcon={<PrintIcon />}
                  onClick={() => printPrescription(patient.medicamentos_activos)}
                  disabled={(patient.medicamentos_activos || []).length === 0}
                  sx={{ color: TEAL, borderColor: TEAL, borderRadius: 2 }}>
                  Imprimir receta
                </Button>
                <Button size="small" variant="outlined" startIcon={<AddIcon />}
                  onClick={() => setMedDlg({ open: true, existing: null })}
                  sx={{ color: PRIMARY, borderColor: PRIMARY, borderRadius: 2 }}>
                  Agregar
                </Button>
              </Box>
            </Box>
            {(patient.medicamentos_activos || []).length === 0 ? (
              <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '2px dashed #E2E8F0', borderRadius: 3 }}>
                <MedicationIcon sx={{ fontSize: 40, color: '#CBD5E1', mb: 1 }} />
                <Typography variant="body2" fontWeight={600} color="text.secondary">Sin medicamentos activos</Typography>
                <Typography variant="caption" color="text.disabled">Agrega medicamentos desde este panel o desde una consulta</Typography>
              </Paper>
            ) : (
              <Stack spacing={1.5}>
                {(patient.medicamentos_activos || []).map((m, i) => {
                  const st = medStatus(m);
                  return (
                    <Paper key={i} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                            <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>{m.nombre}</Typography>
                            <Chip label={st.label} size="small" sx={{ bgcolor: st.bg, color: st.color, fontSize: '0.65rem', height: 18, fontWeight: 700, flexShrink: 0 }} />
                          </Stack>
                          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>{[m.dosis, m.via, m.frecuencia].filter(Boolean).join(' · ')}</Typography>
                          {(m.fecha_inicio || m.fecha_fin) && (
                            <Typography variant="caption" color="text.secondary" display="block">{m.fecha_inicio} → {m.fecha_fin || 'indefinido'}</Typography>
                          )}
                          {m.notas && <Typography variant="caption" color="text.secondary" display="block" sx={{ fontStyle: 'italic', wordBreak: 'break-word' }}>{m.notas}</Typography>}
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <Tooltip title="Imprimir"><IconButton size="small" onClick={() => printPrescription(m)} sx={{ color: TEAL }}><PrintIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                          <Tooltip title="Editar"><IconButton size="small" onClick={() => setMedDlg({ open: true, existing: { ...m, _idx: i } })} sx={{ color: PRIMARY }}><EditIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                          <Tooltip title="Discontinuar"><IconButton size="small" onClick={() => handleDiscontinueMed(i)} sx={{ color: DANGER }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton></Tooltip>
                        </Stack>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}
          </TabPanel>

          {/* ── TAB 3: PREVENTIVOS ──────────────────────────────────────── */}
          <TabPanel value={tab} index={3}>
            {/* Vacunas */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                <VaccinesIcon sx={{ color: TEAL, fontSize: 18 }} />
                <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Vacunas</Typography>
              </Box>
              <Tooltip title="Agregar vacuna">
                <IconButton size="small" onClick={() => setVaccineDlg({ open: true, existing: null })} sx={{ color: TEAL }}>
                  <AddIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
            </Box>
            {(patient.vacunas || []).length === 0 ? (
              <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '2px dashed #E2E8F0', borderRadius: 3, mb: 3 }}>
                <VaccinesIcon sx={{ fontSize: 36, color: '#CBD5E1', mb: 1 }} />
                <Typography variant="body2" fontWeight={600} color="text.secondary">Sin vacunas registradas</Typography>
              </Paper>
            ) : (
              <Stack spacing={1} sx={{ mb: 3 }}>
                {[...(patient.vacunas || [])].sort((a, b) => {
                  if (!a.proxima) return 1; if (!b.proxima) return -1;
                  return new Date(a.proxima) - new Date(b.proxima);
                }).map((v, i) => {
                  const est = proximaEstado(v.proxima);
                  return (
                    <Paper key={i} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0' }}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box>
                          <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                            <Typography variant="body2" fontWeight={700}>{v.vacuna}</Typography>
                            {est && <Chip label={est.label} size="small" sx={{ bgcolor: est.bg, color: est.color, fontSize: '0.65rem', height: 18, fontWeight: 700 }} />}
                          </Stack>
                          <Typography variant="caption" color="text.secondary">Aplicada: {v.fecha}</Typography>
                          {v.proxima && <Typography variant="caption" display="block" fontWeight={600} color={TEAL}>Próxima: {v.proxima}</Typography>}
                          {(v.lote || v.laboratorio) && <Typography variant="caption" color="text.disabled"> Lote: {v.lote || '—'} · Lab: {v.laboratorio || '—'}</Typography>}
                          {v.veterinario && <Typography variant="caption" color="text.disabled" display="block">Aplicó: {v.veterinario}</Typography>}
                        </Box>
                        <Stack direction="row" spacing={0.5}>
                          <IconButton size="small" onClick={() => setVaccineDlg({ open: true, existing: { ...v, _idx: i } })} sx={{ p: 0.3 }}><EditIcon sx={{ fontSize: 13, color: PRIMARY }} /></IconButton>
                          <IconButton size="small" onClick={() => handleDeleteVaccine(i)} sx={{ p: 0.3 }}><DeleteIcon sx={{ fontSize: 13, color: DANGER }} /></IconButton>
                        </Stack>
                      </Box>
                    </Paper>
                  );
                })}
              </Stack>
            )}

            {/* Antiparasitarios */}
            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                  <BugReportIcon sx={{ color: WARNING, fontSize: 18 }} />
                  <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Antiparasitarios</Typography>
                </Box>
                <Tooltip title={`Agregar`}>
                  <IconButton size="small" onClick={() => setAntiDlg({ open: true, existing: null })} sx={{ color: WARNING }}>
                    <AddIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Box>
              {!(patient?.antiparasitarios || []).length ? (
                <Paper elevation={0} sx={{ p: 3, textAlign: 'center', border: '2px dashed #E2E8F0', borderRadius: 3 }}>
                  <Typography variant="caption" color="text.disabled">Sin registros de antiparasitarios</Typography>
                </Paper>
              ) : (
                <Stack spacing={1}>
                  {(patient?.antiparasitarios || []).map((a, i) => {
                    const est = proximaEstado(a.proxima);
                    return (
                      <Paper key={i} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                              <Typography variant="body2" fontWeight={700}>{a.producto}</Typography>
                              {est && <Chip label={est.label} size="small" sx={{ bgcolor: est.bg, color: est.color, fontSize: '0.65rem', height: 18, fontWeight: 700 }} />}
                            </Stack>
                            {a.cantidad && <Typography variant="caption" color="text.secondary">Cantidad: {a.cantidad}</Typography>}
                            <Typography variant="caption" color="text.secondary" display="block">Aplicado: {a.fecha}</Typography>
                            {a.proxima && <Typography variant="caption" fontWeight={600} color={TEAL} display="block">Próximo: {a.proxima}</Typography>}
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton size="small" onClick={() => setAntiDlg({ open: true, existing: { ...a, _idx: i } })} sx={{ p: 0.3 }}><EditIcon sx={{ fontSize: 13, color: PRIMARY }} /></IconButton>
                            <IconButton size="small" onClick={() => handleDeleteAnti(i)} sx={{ p: 0.3 }}><DeleteIcon sx={{ fontSize: 13, color: DANGER }} /></IconButton>
                          </Stack>
                        </Box>
                      </Paper>
                    );
                  })}
                </Stack>
              )}
            </Box>
          </TabPanel>

          {/* ── TAB 4: EXÁMENES ──────────────────────────────────────────── */}
          <TabPanel value={tab} index={4}>
            <Grid container spacing={2}>
              {/* Laboratorio */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                    <ScienceIcon sx={{ color: '#1D4ED8', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Laboratorio</Typography>
                  </Box>
                  <Stack spacing={1}>
                    <Button fullWidth size="small" variant="outlined" startIcon={<ScienceIcon />}
                      onClick={() => navigate(`/lab/history?patientId=${patientId}`)}
                      sx={{ color: PRIMARY, borderColor: '#BFDBFE', fontSize: '0.8rem', py: 1.5 }}>
                      Ver historial de exámenes
                    </Button>
                    <Button fullWidth size="small" variant="outlined" startIcon={<AddIcon />}
                      onClick={() => navigate(`/lab/orders/new?patientId=${patientId}`)}
                      sx={{ color: TEAL, borderColor: '#99F6E4', fontSize: '0.8rem', py: 1.5 }}>
                      Nueva orden de laboratorio
                    </Button>
                  </Stack>
                </Paper>
              </Grid>

              {/* Imágenes */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                    <BrokenImageIcon sx={{ color: '#1D4ED8', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Imágenes</Typography>
                  </Box>
                  <Stack spacing={1}>
                    <Button fullWidth size="small" variant="outlined" startIcon={<BrokenImageIcon />}
                      onClick={() => navigate(`/imaging/history?patientId=${patientId}`)}
                      sx={{ color: PRIMARY, borderColor: '#BFDBFE', fontSize: '0.8rem', py: 1.5 }}>
                      Ver historial de estudios
                    </Button>
                    <Button fullWidth size="small" variant="outlined" startIcon={<AddIcon />}
                      onClick={() => navigate(`/imaging/new?patientId=${patientId}`)}
                      sx={{ color: TEAL, borderColor: '#99F6E4', fontSize: '0.8rem', py: 1.5 }}>
                      Nueva orden de imagen
                    </Button>
                  </Stack>
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          {/* ── TAB 5: CIRUGÍA ───────────────────────────────────────────── */}
          <TabPanel value={tab} index={5}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                    <MedicalServicesIcon sx={{ color: '#DC2626', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Cirugía</Typography>
                  </Box>
                  <Stack spacing={1}>
                    <Button fullWidth size="small" variant="outlined" startIcon={<AddIcon />}
                      onClick={() => navigate(`/surgery/referral?patientId=${patientId}`)}
                      sx={{ color: '#DC2626', borderColor: '#FECACA', fontSize: '0.8rem', py: 1.5 }}>
                      Nueva derivación de cirugía
                    </Button>
                  </Stack>
                </Paper>

                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0', mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                    <LocalHospitalIcon sx={{ color: '#DC2626', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Protocolos quirúrgicos</Typography>
                  </Box>
                  {surgeryProtocols.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Sin protocolos registrados</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {surgeryProtocols.map((p) => (
                        <Paper key={p.id} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>{p.procedureType || 'Cirugía'}</Typography>
                              <Typography variant="caption" color="text.secondary">{fmtDate(p.date)} · {p.mainSurgeon || '—'}</Typography>
                            </Box>
                            <Button size="small" variant="outlined" startIcon={<MedicalServicesIcon />}
                              onClick={() => navigate(`/surgery-protocol/${p.surgeryId || p.id}`)}
                              sx={{ color: '#DC2626', borderColor: '#FECACA', fontSize: '0.7rem' }}>
                              Ver ficha
                            </Button>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                    <LocalHospitalIcon sx={{ color: '#DC2626', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Derivaciones a cirugía</Typography>
                  </Box>
                  {surgeryRequests.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Sin derivaciones registradas</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {surgeryRequests.map((r) => (
                        <Paper key={r.id} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>{r.proposedProcedure || 'Cirugía'}</Typography>
                              <Typography variant="caption" color="text.secondary">{fmtDate(r.createdAt)} · {r.priority || '—'} · {r.status || '—'}</Typography>
                            </Box>
                            <Button size="small" variant="outlined" startIcon={<MedicalServicesIcon />}
                              onClick={() => navigate(`/surgery/referral?id=${r.id}`)}
                              sx={{ color: '#DC2626', borderColor: '#FECACA', fontSize: '0.7rem' }}>
                              Ver ficha
                            </Button>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          {/* ── TAB 6: HOSPITAL ──────────────────────────────────────────── */}
          <TabPanel value={tab} index={6}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                    <LocalHospitalIcon sx={{ color: '#1D4ED8', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Hospital</Typography>
                  </Box>
                  <Stack spacing={1}>
                    <Button fullWidth size="small" variant="outlined" startIcon={<LocalHospitalIcon />}
                      disabled={!activeHosp}
                      onClick={() => { if (activeHosp) navigate(`/hospital-flowsheet/${activeHosp.id}`); }}
                      sx={{ color: PRIMARY, borderColor: '#BFDBFE', fontSize: '0.8rem', py: 1.5 }}>
                      Ver hoja de tratamiento
                    </Button>
                    <Button fullWidth size="small" variant="outlined" startIcon={<AddIcon />}
                      disabled={isDeceased || !!activeHosp || pendingHospOrders.length > 0}
                      onClick={() => navigate(`/hospital?patientId=${patientId}&newOrder=true`)}
                      sx={{ color: TEAL, borderColor: '#99F6E4', fontSize: '0.8rem', py: 1.5 }}>
                      {isDeceased ? 'Paciente fallecido' : activeHosp ? 'Paciente hospitalizado' : pendingHospOrders.length > 0 ? 'Orden pendiente de asignación' : 'Ingresar a hospital'}
                    </Button>
                  </Stack>
                </Paper>

                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0', mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                    <LocalHospitalIcon sx={{ color: '#1D4ED8', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Episodios de hospitalización</Typography>
                  </Box>
                  {hospitalizations.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Sin episodios de hospitalización</Typography>
                  ) : (
                    <Stack spacing={1}>
                      {hospitalizations.map((h) => (
                        <Paper key={h.id} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="body2" fontWeight={700}>{fmtDateTime(h.admissionAt)}</Typography>
                              <Typography variant="caption" color="text.secondary">Estado: {h.status || h.clinicalStatus || '—'} · {calcHospDays(h.admissionAt, h.altaAt)} día(s)</Typography>
                              <Typography variant="caption" color="text.secondary" display="block">Diagnóstico: {h.altaDiagnosticoEgreso || h.primaryDiagnosis || '—'}</Typography>
                            </Box>
                            <Stack direction="row" spacing={0.5}>
                              {(h.epicrisis || h.status === 'alta') && (
                                <Button size="small" variant="outlined" startIcon={<InsertDriveFileIcon />}
                                  onClick={() => navigate(`/hospital-epicrisis/${h.id}`)}
                                  sx={{ color: TEAL, borderColor: '#99F6E4', fontSize: '0.7rem' }}>
                                  Ver epicrisis
                                </Button>
                              )}
                              <Button size="small" variant="outlined" startIcon={<LocalHospitalIcon />}
                                onClick={() => navigate(`/hospital-flowsheet/${h.id}`)}
                                sx={{ color: PRIMARY, borderColor: '#BFDBFE', fontSize: '0.7rem' }}>
                                Ver ficha
                              </Button>
                            </Stack>
                          </Box>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </TabPanel>

          {/* ── TAB 7: INTERCONSULTA ───────────────────────────────────────── */}
          <TabPanel value={tab} index={7}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                    <MedicalServicesIcon sx={{ color: '#1D4ED8', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Nueva indicación</Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" fontWeight={600} color={TEAL} sx={{ mb: 1 }}>Derivación a especialidad</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {specialties.map((s) => (
                        <FormControlLabel key={s.id}
                          control={<Checkbox size="small" checked={interForm.specialtyIds.includes(s.id)} onChange={toggleInterSpecialty(s.id)} />}
                          label={<Typography variant="caption">{s.name}</Typography>}
                        />
                      ))}
                      {specialties.length === 0 && <Typography variant="caption" color="text.secondary">Sin especialidades configuradas</Typography>}
                    </Box>
                  </Box>
                  <TextField label="Indicaciones y notas" fullWidth multiline rows={4} value={interForm.notas} onChange={(e) => setInterForm((p) => ({ ...p, notas: e.target.value }))} sx={{ mb: 2 }} />
                  <TextField label="Solicitud de exámenes" fullWidth multiline rows={3} value={interForm.examenes} onChange={(e) => setInterForm((p) => ({ ...p, examenes: e.target.value }))} sx={{ mb: 2 }} helperText="Exámenes que no se prestan en el hospital y deben solicitarse en otra clínica" />
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                    <Button variant="outlined" onClick={() => setInterForm({ specialtyIds: [], notas: '', examenes: '' })} disabled={savingInter}>Limpiar</Button>
                    <Button variant="contained" onClick={handleSaveInter} disabled={savingInter} sx={{ bgcolor: PRIMARY }}>{savingInter ? 'Guardando…' : 'Guardar'}</Button>
                  </Box>
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle1" fontWeight={700} color={PRIMARY} sx={{ mb: 1.5 }}>Historial de indicaciones</Typography>
                {!(patient?.interconsultas || []).length ? (
                  <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '2px dashed #E2E8F0', borderRadius: 3 }}>
                    <MedicalServicesIcon sx={{ fontSize: 40, color: '#CBD5E1', mb: 1 }} />
                    <Typography variant="body2" fontWeight={600} color="text.secondary">Sin indicaciones registradas</Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1.5}>
                    {(patient?.interconsultas || []).map((ic, i) => (
                      <Paper key={ic.id || i} elevation={0} sx={{ p: 2, borderRadius: 2, border: '1px solid #E2E8F0' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1 }}>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="body2" fontWeight={700}>{ic.fecha}</Typography>
                            {ic.specialtyNames?.length > 0 && <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>Especialidades: {ic.specialtyNames.join(', ')}</Typography>}
                            {ic.notas && <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>Notas: {ic.notas}</Typography>}
                            {ic.examenes && <Typography variant="caption" color="text.secondary" display="block" sx={{ wordBreak: 'break-word' }}>Exámenes: {ic.examenes}</Typography>}
                          </Box>
                          <Stack direction="row" spacing={0.5}>
                            <IconButton size="small" onClick={() => printInter(ic)} sx={{ color: TEAL }}><PrintIcon sx={{ fontSize: 14 }} /></IconButton>
                            <IconButton size="small" onClick={() => handleDeleteInter(i)} sx={{ color: DANGER }}><DeleteIcon sx={{ fontSize: 14 }} /></IconButton>
                          </Stack>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Grid>
            </Grid>
          </TabPanel>

          {/* ── TAB 8: ARCHIVOS ──────────────────────────────────────────────── */}
          <TabPanel value={tab} index={8}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 3, border: '1px solid #E2E8F0' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                    <CloudUploadIcon sx={{ color: '#1D4ED8', fontSize: 20 }} />
                    <Typography variant="subtitle1" fontWeight={700} color={PRIMARY}>Subir archivo</Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Adjunta imágenes (JPG/PNG) o PDF del paciente.
                  </Typography>
                  <Button
                    variant="contained"
                    component="label"
                    startIcon={<CloudUploadIcon />}
                    disabled={uploading}
                    sx={{ bgcolor: PRIMARY }}
                  >
                    {uploading ? `Subiendo ${uploadProgress}%` : 'Seleccionar archivo'}
                    <input type="file" accept=".jpg,.jpeg,.png,.pdf" hidden onChange={handleFileUpload} />
                  </Button>
                  {uploading && (
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ height: 6, bgcolor: '#E2E8F0', borderRadius: 1, overflow: 'hidden' }}>
                        <Box sx={{ height: '100%', width: `${uploadProgress}%`, bgcolor: TEAL, transition: 'width 0.2s' }} />
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Typography variant="subtitle1" fontWeight={700} color={PRIMARY} sx={{ mb: 1.5 }}>Archivos adjuntos</Typography>
                {archivos.length === 0 ? (
                  <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '2px dashed #E2E8F0', borderRadius: 3 }}>
                    <InsertDriveFileIcon sx={{ fontSize: 40, color: '#CBD5E1', mb: 1 }} />
                    <Typography variant="body2" fontWeight={600} color="text.secondary">Sin archivos adjuntos</Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1.5}>
                    {archivos.map((a) => (
                      <Paper key={a.id} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid #E2E8F0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          {a.type?.startsWith('image/') ? (
                            <Box
                              component="img"
                              src={a.url}
                              alt={a.name}
                              onClick={() => window.open(a.url, '_blank')}
                              sx={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 1, cursor: 'pointer' }}
                            />
                          ) : (
                            <PictureAsPdfIcon sx={{ fontSize: 40, color: '#DC2626' }} />
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700} noWrap sx={{ cursor: 'pointer' }} onClick={() => window.open(a.url, '_blank')}>
                              {a.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {a.uploadedBy} · {a.uploadedAt?.toDate ? a.uploadedAt.toDate().toLocaleDateString('es-CL') : '—'}
                            </Typography>
                          </Box>
                          <Tooltip title="Eliminar">
                            <IconButton size="small" onClick={() => handleDeleteArchivo(a)} sx={{ color: DANGER }}>
                              <DeleteIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Grid>
            </Grid>
          </TabPanel>

        </Box>
      </Paper>
      {/* ══ DIALOGS ══════════════════════════════════════════════════════════ */}
      <MedicalProfileDialog open={profileDlg} patientId={patientId} patient={patient}
        onClose={(saved) => { setProfileDlg(false); if (saved) loadPatient(); }} />
      <VaccineDialog open={vaccineDlg.open} patientId={patientId} patient={patient} owner={owner} existing={vaccineDlg.existing}
        onClose={(saved) => { setVaccineDlg({ open: false, existing: null }); if (saved) loadPatient(); }} />
      <AntiparasiticDialog open={antiDlg.open} patientId={patientId} patient={patient} owner={owner} existing={antiDlg.existing}
        onClose={(saved) => { setAntiDlg({ open: false, existing: null }); if (saved) loadPatient(); }} />
      <MedicationActiveDialog open={medDlg.open} patientId={patientId} patient={patient} existing={medDlg.existing}
        onClose={(saved) => { setMedDlg({ open: false, existing: null }); if (saved) loadPatient(); }} />
      <DeceasedRegistrationDialog open={deceasedDlg} patientId={patientId}
        onClose={() => setDeceasedDlg(false)} onSaved={loadPatient} />
      <AnularDefuncionDialog open={anularDlg} patientId={patientId}
        onClose={() => setAnularDlg(false)} onSaved={loadPatient} />
    </Box>
  );
}
